import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./ManagePayFlow.css";
import { useAuth } from "../hooks/useAuth";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { parseStaffAccountsPdf, staffAccountEntryIndexFor } from "./staffAccountsPdf";
import {
  usePayflowStore,
  payflowActions,
  refreshFromServer,
  computeRow,
  exceptionSummary,
  periodStatus,
  periodHasPending,
  periodHasOutstandingTasks,
  isStatHoliday,
  breakDeductionFor,
  fixedHolidayISO,
  holidayDraftKey,
  ONTARIO_HOLIDAYS,
  tipoutFromSales,
  EMPTY_SHIFT_SALES,
  type Tipout,
  rolesFromPeriods,
  employeesFromPeriods,
  isEventAccount,
  isHiddenAccount,
  isTakeoutAccount,
  eventAccountName,
  partyKey,
  poolKeyForWindow,
  poolWindowSpan,
  POOL_META,
  type PoolKey,
  rowKey,
  fmtTime,
  fmtTimeOrNone,
  fmtHours,
  fmtSigned,
  fmtMoney,
  fmtISODate,
  ymdFromISO,
  inClass,
  outClass,
  deltaClass,
  type Day,
  type PayPeriod,
  type PayPeriodStatus,
  type RowComputation,
  type TipRates,
} from "./payflowStore";
import { type DataCoverage, type PayflowRules } from "./payflowApi";
import PullRangeCalendar from "./PullRangeCalendar";
import { daysBetweenISO, isoAddDays, isoOf } from "./payflowDates";

// Tip-calculation rate rows (label + explanation), keyed to TipRates.
const TIP_RATE_DEFS: { key: keyof TipRates; label: string; hint: string }[] = [
  {
    key: "creditCardFee",
    label: "Credit Card Fee",
    hint: "All tips paid by credit card are subject to this fee.",
  },
  {
    key: "tipPool",
    label: "Tip Pool",
    hint: "All Bartenders and Servers must contribute this amount to the Tip Pool.",
  },
  {
    key: "hostFoodRunners",
    label: "Host / Food Runner Pool",
    hint: "All Bartenders and Servers must contribute this amount to the Host/Food Runner Pool.",
  },
  {
    key: "serverToBar",
    label: "Bar Pool",
    hint: "All Servers contribute this amount of total sales to the bartender pool.",
  },
  {
    key: "barBackPool",
    label: "BarBack Pool",
    hint: "All Bartenders contribute this amount of total sales to the bartender pool.",
  },
];

// One Overview line's computed figures (per employee + role) — the return of
// summaryLineFor and the shape frozen at Overview verification. Named explicitly (rather
// than inferred) so the frozen lookup and the function that produces it share one type.
interface OverviewLine {
  ovKey: string;
  rate: number;
  tipPoolHr: number;
  foh: boolean;
  fohOn: boolean;
  base: number;
  payTipPool: number;
  payFoh: number;
  statAmt: number;
  workedStat: boolean;
  vacation: number;
  staffAcct: number | undefined;
  carriesStaffAcct: boolean;
}

// Tooltip text for the Adjust chip, by state.
const adjustTooltip = (c: RowComputation): string => {
  if (c.isManual) return `Admin adjustment: ${c.reason || "—"} (click to edit)`;
  if (c.status === "confirmed" && c.confirmedIn !== undefined && c.confirmedOut !== undefined)
    return `Manager confirmed ${fmtTime(c.confirmedIn)} – ${fmtTime(c.confirmedOut)}: ${c.reason} (click to override)`;
  return "Click to add a manual adjustment";
};

const STATUS_LABEL: Record<PayPeriodStatus, string> = {
  pending: "Pending",
  ready: "Verify Hours",
  approved: "Verify Tips",
  tipsverified: "Process",
  processed: "Processed",
};

const isKitchenRole = (role: string) => /kitchen|cook|chef|dish/i.test(role);

// Bar-location pool tints for the Tips sheet rows. Each bar has its own hue;
// day/night are the same hue at different strengths so a bar's day pool and
// night pool read as separate groups (they ARE separate pools).
const LOCATION_TINT: Record<
  string,
  { color: string; tintDay: string; tintNight: string }
> = {
  "Main Bar": { color: "#3b82f6", tintDay: "rgba(59,130,246,0.10)", tintNight: "rgba(59,130,246,0.24)" },
  Parlour: { color: "#a855f7", tintDay: "rgba(168,85,247,0.10)", tintNight: "rgba(168,85,247,0.24)" },
  Patio: { color: "#14b8a6", tintDay: "rgba(20,184,166,0.10)", tintNight: "rgba(20,184,166,0.24)" },
};

const periodEnd = (p: PayPeriod): string => fmtISODate(p.endISO);

// A selectable pay-period ending date. `exists` marks a date that already has a
// payroll — picking it rebuilds (replaces) that payroll.
interface PeriodOption {
  iso: string;
  label: string;
  exists: boolean;
}

// The end date of the most recently CLOSED pay period: the newest anchor-aligned
// boundary strictly before today.
//
// This is the ceiling on what a pull may ask for AND the newest date the add-period
// list offers. The pay period still running has days nothing has happened on yet, so
// pulling into it fetches a period that is still moving. Derived from the same anchor
// and length that drive the period list, so what can be pulled and what can be built
// can never disagree.
//
// Strictly before today, not "not in the future": the business day rolls at ~04:59
// rather than midnight, so a period ending today is still taking shifts. Including
// it would let an admin pull a fortnight whose last day is half-recorded and then
// build a payroll from it.
const lastClosedPeriodEndISO = (
  anchorEndISO: string,
  lengthDays: number,
  today: Date = new Date(),
): string => {
  const step = lengthDays > 0 ? lengthDays : 14;
  const anchor = ymdFromISO(anchorEndISO);
  const cutoff = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const d = new Date(anchor.year, anchor.monthIndex, anchor.day);
  // The configured anchor may sit either side of today, so walk it in whichever
  // direction is needed rather than assuming it is already in the past.
  while (d >= cutoff) d.setDate(d.getDate() - step);
  for (;;) {
    const next = new Date(d);
    next.setDate(next.getDate() + step);
    if (next >= cutoff) break;
    d.setTime(next.getTime());
  }
  return isoOf(d);
};

// Every period-ending date from the most recently CLOSED period back 18 months,
// aligned to the server's pay-period anchor and length. The still-running period is
// deliberately excluded — a period cannot be added until it has ended (its days are
// not all recorded yet), so an end date only appears the day after it closes.
const fortnightlyEndOptions = (
  anchorEndISO: string,
  lengthDays: number,
  existingISO: Set<string>,
): PeriodOption[] => {
  const step = lengthDays > 0 ? lengthDays : 14;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Start at the newest anchor-aligned boundary strictly before today (the last
  // closed period), the same ceiling that governs what can be pulled.
  const start = ymdFromISO(lastClosedPeriodEndISO(anchorEndISO, lengthDays, today));
  const horizon = new Date(today);
  horizon.setMonth(horizon.getMonth() - 18);
  const out: PeriodOption[] = [];
  const d = new Date(start.year, start.monthIndex, start.day);
  while (d >= horizon) {
    const iso = isoOf(d);
    out.push({
      iso,
      label: d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
      exists: existingISO.has(iso),
    });
    d.setDate(d.getDate() - step);
  }
  return out;
};

// The pipeline refuses a window longer than RECON_MAX_WINDOW_DAYS (default 60) with
// a 400. Mirrored here so an over-long range is a message under the field rather than
// a round trip that comes back as a server error.
//
// A copy of a number the pipeline owns (variance-etl-pipeline/app/config.py). If that
// setting is ever lowered, this stays generous and the over-long range round-trips —
// the server's own 400 message is passed through, so the admin still learns the real
// limit, but the two should be changed together.
const MAX_PULL_SPAN_DAYS = 15;

// Why a chosen range cannot be pulled, or null when it can. One function so the
// message under the fields and the disabled state of the button can never disagree.
const pullRangeProblem = (
  startISO: string,
  endISO: string,
  latestAllowedISO: string,
): string | null => {
  if (!startISO || !endISO) return "Choose both a start and an end date.";
  if (startISO > endISO) return "The start date must be on or before the end date.";
  if (endISO > latestAllowedISO) {
    return `The most recently closed pay period ends ${fmtISODate(
      latestAllowedISO,
    )}. Anything after that is still open and cannot be pulled yet.`;
  }
  const span = daysBetweenISO(startISO, endISO) + 1;
  if (span > MAX_PULL_SPAN_DAYS) {
    return `That range covers ${span} days — the most that can be pulled at once is ${MAX_PULL_SPAN_DAYS}.`;
  }
  return null;
};

// A run of pipe with a flange at each end and a bend between them. Decorative:
// the button that carries it supplies the accessible name.
const PipelineIcon = () => (
  <svg
    className="payflow-pull-icon"
    viewBox="0 0 24 24"
    width="17"
    height="17"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 7h4a4 4 0 0 1 4 4v2a4 4 0 0 0 4 4h4" />
    <rect x="1.5" y="4" width="3" height="6" rx="1" />
    <rect x="19.5" y="14" width="3" height="6" rx="1" />
  </svg>
);

const periodSortKey = (p: PayPeriod): number =>
  p.start.year * 10000 + p.start.monthIndex * 100 + p.start.day;
// A period is filed under the year of its last day (the pay-period end).
const periodYear = (p: PayPeriod): number => Number(p.endISO.slice(0, 4));

// The two stages a payroll build moves through, shown in the progress modal.
// A build no longer has steps worth showing. It reads data already stored and
// assembles a fortnight from it, so it finishes in seconds and the only states are
// "doing it" and "done" — the five-step timeline that used to live here existed to
// make a stalled PULL diagnostic, and pulling is now its own action with its own
// progress (see the pull modal below).

// How fresh the stored data is, phrased for someone deciding whether to pull before
// building. Null when the sources have never reported, which is not an error — the
// watermarks are best-effort and a first-ever pull has nothing to report yet.
function coverageSummary(coverage: DataCoverage[]): string | null {
  const pulled = coverage
    .map((c) => c.lastPulledAt)
    .filter((at): at is string => !!at)
    .map((at) => Date.parse(at))
    .filter((ms) => Number.isFinite(ms));
  if (!pulled.length) return null;
  // The OLDEST source is the honest answer: data is only as current as its stalest
  // half, and a payroll built now would be assembled from both.
  const oldest = new Date(Math.min(...pulled));
  const failing = coverage.filter((c) => !c.ok).map((c) => c.name);
  const stamp = `Data last updated ${oldest.toLocaleString()}`;
  return failing.length ? `${stamp} · ${failing.join(" and ")} last failed` : stamp;
}

interface AdjustModalState {
  key: string;
  employee: string;
  dayLabel: string;
  schedTotal: number;
  value: string; // signed hours vs scheduled
  reason: string;
  isExisting: boolean; // an admin adjustment already exists (allow Remove)
}

export default function ManagePayFlow() {
  const {
    payPeriods,
    hydrated,
    syncError,
    coverageGap,
    building,
    dataPull,
    dataPullError,
    dataPullWatch,
    dateCoverage,
    dateCoverageLoaded,
    adjustments,
    reviews,
    // The LIVE rules, as they stand on the Payroll Rules tab right now.
    //
    // ONLY the Rules tab and the pickers that seed from it may read these. Everything
    // that computes a figure for a pay period reads that period's own snapshot
    // instead — see the shadow below `activePeriod`, which rebinds the unprefixed
    // names so no math site had to change. Reading a `live*` name inside a period
    // view is the one way to reintroduce retroactive rule edits.
    committedHolidays: liveCommittedHolidays,
    holidayDrafts,
    breakRules: liveBreakRules,
    breakRulesLock: liveBreakRulesLock,
    tipRates: liveTipRates,
    tipRatesLock: liveTipRatesLock,
    fohRoles: liveFohRoles,
    fohRolesLock: liveFohRolesLock,
    salariedEmployeeIds: liveSalariedEmployeeIds,
    salariedLock: liveSalariedLock,
    payConstants: livePayConstants,
    payPeriod,
    hoursApproved,
    tipsApproved,
    poolSolo,
    processedPeriods,
    summaryOverrides,
    manualPools,
    barAllocations,
    staffAccounts,
    registers,
    frozenStages,
    auditLog,
  } = usePayflowStore();
  const { user } = useAuth();
  const currentUser: string = user?.name || user?.email || "Unknown user";
  // Roles available to pick from (from the schedule/POS). The FOH-eligibility test
  // that goes with it is defined below the rules shadow, because it feeds the
  // Summary/Payroll FOH premium and so must read the open period's snapshot.
  const availableRoles = useMemo(() => rolesFromPeriods(payPeriods), [payPeriods]);
  // Employees available to flag as salaried (from the loaded payrolls).
  const availableEmployees = useMemo(() => employeesFromPeriods(payPeriods), [payPeriods]);
  // View routing (string-keyed): "overview" | "rules" | "year:<YYYY>" |
  // "period:<id>". The pay-period sheet is reached via a View button, not a tab.
  const [view, setView] = useState<string>("overview");
  const [search, setSearch] = useState("");
  const [addPeriodOpen, setAddPeriodOpen] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState<PeriodOption | null>(null);
  // The build keeps running server-side when the progress modal is hidden.
  const [buildHidden, setBuildHidden] = useState(false);
  const [pullOpen, setPullOpen] = useState(false);
  // Whether this tab was following a pull of its own on the previous render.
  //
  // A ref rather than state because it is a memory, not something to draw: writing it
  // must not cause the render that would then re-write it. The store reports what is
  // true NOW, so "the pull just finished" is not a value it can hand over — it only
  // exists as the difference between two renders, and this is what holds the other half
  // of that comparison.
  const wasWatchingOwnPull = useRef(false);
  // A pull is org-wide, so "is one running" is the server's answer, never a local flag
  // — this screen can be watching one the nightly schedule or another admin started.
  const pullRunning = dataPull?.state === "running";
  // What the modal keys off, and it is deliberately NOT just the server's answer.
  // `dataPullWatch` is this tab's own run, held from the click until that run is
  // confirmed finished — so a status the server cannot read, or one that has not caught
  // up with the run we started, keeps the progress view up instead of dropping it.
  // Dropping it was the bug: the modal flashed and reverted to the calendar while the
  // pull carried on running.
  const pullInFlight = pullRunning || dataPullWatch !== null;
  // Before the 202 comes back there is no run id, so nothing can be reported about the
  // run itself — only that we are asking for it.
  const pullStarting = dataPullWatch?.phase === "starting";
  // Started, but the run doc has not shown us this run yet. Worth saying out loud: it
  // is the state that used to end the watch silently.
  const pullConfirming = dataPullWatch?.phase === "confirming";
  // The steps to draw, straight from the server. Only for the run this modal is
  // actually watching: a finished run's stages still describe it, and leaving them up
  // would make a stale timeline look live the next time the modal opens.
  const stages = pullRunning ? (dataPull?.stages ?? []) : [];
  const coverageLine = useMemo(
    () => coverageSummary(dataPull?.coverage ?? []),
    [dataPull?.coverage],
  );
  // Referentially stable so the calendar's "this month is visible" effect does not
  // re-fire on every render of this screen.
  const loadDateCoverage = useCallback(
    (monthKey: string, startISO: string, endISO: string) =>
      void payflowActions.loadDateCoverage(monthKey, startISO, endISO),
    [],
  );

  /* ── The pull window ──────────────────────────────────────────────────────
     An admin chooses which dates to fetch rather than accepting whatever the
     pipeline's default sliding window happens to be. The ceiling is the end of
     the most recently closed pay period — see lastClosedPeriodEndISO. */

  // The date the ceiling was computed against, re-stamped every time the modal
  // opens. Without it, `new Date()` would be captured once when the screen mounted
  // and a tab left open across a pay-period boundary would keep offering the
  // fortnight before last, with no cue that a reload was needed.
  const [pullAsOfISO, setPullAsOfISO] = useState(() => isoOf(new Date()));
  const latestPullDate = useMemo(() => {
    const { year, monthIndex, day } = ymdFromISO(pullAsOfISO);
    return lastClosedPeriodEndISO(
      payPeriod.anchorEnd,
      payPeriod.lengthDays,
      new Date(year, monthIndex, day),
    );
  }, [payPeriod.anchorEnd, payPeriod.lengthDays, pullAsOfISO]);
  // Defaults to exactly that closed period, which is what an admin is nearly always
  // after: the fortnight they are about to build a payroll for.
  const defaultPullRange = useMemo(
    () => ({
      start: isoAddDays(
        latestPullDate,
        -((payPeriod.lengthDays > 0 ? payPeriod.lengthDays : 14) - 1),
      ),
      end: latestPullDate,
    }),
    [latestPullDate, payPeriod.lengthDays],
  );
  const [pullStart, setPullStart] = useState(defaultPullRange.start);
  const [pullEnd, setPullEnd] = useState(defaultPullRange.end);
  const pullRangeError = pullRangeProblem(pullStart, pullEnd, latestPullDate);
  // When the modal is opened for a specific period (from the "no data for that
  // period" banner), the range to seed instead of the default fortnight. Cleared
  // on close so a later manual open goes back to the default refresh window.
  const [pullSeedRange, setPullSeedRange] = useState<{ start: string; end: string } | null>(
    null,
  );
  // A payroll to build automatically once this pull lands clean — set when the
  // pull was opened to fill a coverage gap, so the admin doesn't have to click
  // Add Pay Period a second time.
  const [pendingBuildEnd, setPendingBuildEnd] = useState<string | null>(null);

  // Seeding lives with the open state rather than in the click handler, so any
  // future way of opening the modal gets the same fresh dates. Opening re-stamps
  // "today" first; the seed below then runs against the recomputed ceiling.
  useEffect(() => {
    if (!pullOpen) return;
    setPullAsOfISO(isoOf(new Date()));
  }, [pullOpen]);

  useEffect(() => {
    if (!pullOpen) {
      // Closing drops the period-specific seed and any pending build that never
      // completed — a plain refresh next time, with no stale auto-build waiting.
      setPullSeedRange(null);
      setPendingBuildEnd(null);
      return;
    }
    const seed = pullSeedRange ?? defaultPullRange;
    setPullStart(seed.start);
    setPullEnd(seed.end);
  }, [pullOpen, defaultPullRange.start, defaultPullRange.end, pullSeedRange]);

  // Opens the Pull Data modal scoped to a period that had no data, and lines up
  // that period's payroll to build as soon as the pull lands. The one-click path
  // out of the fresh-start wall: a cold period isn't in the pipeline yet, so the
  // build can't fetch it — this pulls exactly that fortnight, then builds it.
  const pullForPeriod = (endISO: string) => {
    const days = payPeriod.lengthDays > 0 ? payPeriod.lengthDays : 14;
    setPullSeedRange({ start: isoAddDays(endISO, -(days - 1)), end: endISO });
    setPendingBuildEnd(endISO);
    setPullOpen(true);
    // Drops the banner (and the coverage gap it read from) — captured above.
    payflowActions.clearSyncError();
  };
  const [deletePeriodTarget, setDeletePeriodTarget] = useState<PayPeriod | null>(null);
  const [adjustModal, setAdjustModal] = useState<AdjustModalState | null>(null);
  const [holidayCommitOpen, setHolidayCommitOpen] = useState(false);
  const [tipCommitOpen, setTipCommitOpen] = useState(false);
  const [unlockTipReason, setUnlockTipReason] = useState<string | null>(null);
  const [breakCommitOpen, setBreakCommitOpen] = useState(false);
  const [unlockBreakReason, setUnlockBreakReason] = useState<string | null>(null);
  const [fohEditOpen, setFohEditOpen] = useState(false);
  const [fohDraft, setFohDraft] = useState<string[]>([]);
  const [fohCommitOpen, setFohCommitOpen] = useState(false);
  const [unlockFohReason, setUnlockFohReason] = useState<string | null>(null);
  const [salariedEditOpen, setSalariedEditOpen] = useState(false);
  const [salariedDraft, setSalariedDraft] = useState<string[]>([]);
  const [salariedCommitOpen, setSalariedCommitOpen] = useState(false);
  const [unlockSalariedReason, setUnlockSalariedReason] = useState<string | null>(null);
  const [historyPeriod, setHistoryPeriod] = useState<PayPeriod | null>(null);
  const [unlockTipsVerifyReason, setUnlockTipsVerifyReason] = useState<string | null>(null);
  const [unlockReason, setUnlockReason] = useState<string | null>(null); // holidays reopen
  const [unlockHoursReason, setUnlockHoursReason] = useState<string | null>(null); // net-hours unlock
  const [unlockProcessReason, setUnlockProcessReason] = useState<string | null>(null); // un-process
  const [verifyWarning, setVerifyWarning] = useState<{ title: string; reasons: string[] } | null>(
    null,
  );
  // Which Event party rows are expanded on the Tips tab (keyed `${dayIndex}:${eventName}`).
  const [expandedParties, setExpandedParties] = useState<Record<string, boolean>>({});
  const [staffWarnOpen, setStaffWarnOpen] = useState(false);
  // "Upload or Skip" choice shown before the file picker, so a period with no
  // staff-account activity can be closed out without a file.
  const [staffChoiceOpen, setStaffChoiceOpen] = useState(false);
  const staffFileRef = useRef<HTMLInputElement | null>(null);
  const [summaryEdit, setSummaryEdit] = useState<{
    key: string;
    field: "rate" | "tipPool" | "staffAccount";
    label: string;
    employee: string;
    value: string;
  } | null>(null);
  const [periodTab, setPeriodTab] = useState<"hours" | "tips" | "summary" | "payroll">(
    "hours",
  );
  const [helpOpen, setHelpOpen] = useState<Record<string, boolean>>({});
  const toggleHelp = (key: string) =>
    setHelpOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const currentYear = new Date().getFullYear();
  const todayISO = new Date().toISOString().slice(0, 10);
  const committedList = liveCommittedHolidays[String(currentYear)] || [];
  const committedMap = Object.fromEntries(committedList.map((h) => [h.key, h.date]));
  const anyCommitted = committedList.length > 0;

  // Per-holiday resolution: a holiday is "locked" if it's in the committed
  // list; otherwise it's editable (fixed → computed date, variable → draft).
  const holidayRows = ONTARIO_HOLIDAYS.map((def) => {
    const fixedISO = fixedHolidayISO(def, currentYear);
    const lockedDate = committedMap[def.key];
    const locked = lockedDate != null;
    const date = locked
      ? lockedDate
      : fixedISO ?? holidayDrafts[holidayDraftKey(currentYear, def.key)] ?? "";
    return { def, fixedISO, date, locked };
  });
  const hasUnlockedRows = holidayRows.some((r) => !r.locked);
  const allHolidaysAssigned = holidayRows.every((r) => !!r.date);
  // Only future locked dates can be reopened; passed ones are used in payroll.
  const hasFutureLocked = holidayRows.some((r) => r.locked && r.date >= todayISO);
  const archivedYears = Object.keys(liveCommittedHolidays)
    .filter((y) => Number(y) !== currentYear)
    .sort()
    .reverse();

  const commitHolidays = () => {
    payflowActions.commitHolidays(
      currentYear,
      holidayRows.map((r) => ({ key: r.def.key, name: r.def.name, date: r.date })),
      currentUser,
    );
    setHolidayCommitOpen(false);
  };

  const confirmReopen = () => {
    if (!unlockReason || !unlockReason.trim()) return;
    payflowActions.reopenHolidays(currentYear, unlockReason.trim(), currentUser, todayISO);
    setUnlockReason(null);
  };

  const commitTipRates = () => {
    payflowActions.commitTipRates(currentUser);
    setTipCommitOpen(false);
  };

  const confirmUnlockTips = () => {
    if (!unlockTipReason || !unlockTipReason.trim()) return;
    payflowActions.unlockTipRates(unlockTipReason.trim(), currentUser);
    setUnlockTipReason(null);
  };

  const commitBreakRules = () => {
    payflowActions.commitBreakRules(currentUser);
    setBreakCommitOpen(false);
  };

  const confirmUnlockBreak = () => {
    if (!unlockBreakReason || !unlockBreakReason.trim()) return;
    payflowActions.unlockBreakRules(unlockBreakReason.trim(), currentUser);
    setUnlockBreakReason(null);
  };

  // FOH-eligible roles: edit (checkbox picker) → save draft → commit-lock.
  const openFohEdit = () => {
    setFohDraft([...liveFohRoles]);
    setFohEditOpen(true);
  };

  const toggleFohDraftRole = (role: string) => {
    setFohDraft((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const saveFohEdit = () => {
    payflowActions.setFohRoles(fohDraft);
    setFohEditOpen(false);
  };

  const commitFohRoles = () => {
    payflowActions.commitFohRoles(currentUser);
    setFohCommitOpen(false);
  };

  const confirmUnlockFoh = () => {
    if (!unlockFohReason || !unlockFohReason.trim()) return;
    payflowActions.unlockFohRoles(unlockFohReason.trim(), currentUser);
    setUnlockFohReason(null);
  };

  const openSalariedEdit = () => {
    setSalariedDraft([...liveSalariedEmployeeIds]);
    setSalariedEditOpen(true);
  };

  const toggleSalariedDraft = (id: string) => {
    setSalariedDraft((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const saveSalariedEdit = () => {
    payflowActions.setSalariedEmployees(salariedDraft);
    setSalariedEditOpen(false);
  };

  const commitSalaried = () => {
    payflowActions.commitSalaried(currentUser);
    setSalariedCommitOpen(false);
  };

  const confirmUnlockSalaried = () => {
    if (!unlockSalariedReason || !unlockSalariedReason.trim()) return;
    payflowActions.unlockSalaried(unlockSalariedReason.trim(), currentUser);
    setUnlockSalariedReason(null);
  };

  const verifyTips = () => {
    if (!activePeriod || periodOutstanding || tipsVerified) return;
    payflowActions.approveTips(
      activePeriod.id,
      currentUser,
      `Pay period ending ${periodEnd(activePeriod)}`,
      buildTipsFreeze(),
    );
    // Verifying tips advances the workflow to the Summary screen.
    setPeriodTab("summary");
  };

  const processPayroll = () => {
    if (!activePeriod || !tipsVerified || periodProcessed) return;
    payflowActions.processPeriod(
      activePeriod.id,
      currentUser,
      `Pay period ending ${periodEnd(activePeriod)}`,
      // The figures exactly as this screen has them at the moment Process is pressed.
      // Recorded as what was paid, so a later change to the payroll engine cannot
      // rewrite what this payroll says.
      buildRegister(),
    );
  };

  const confirmUnlockProcess = () => {
    if (!activePeriod || !unlockProcessReason || !unlockProcessReason.trim()) return;
    payflowActions.unprocessPeriod(
      activePeriod.id,
      unlockProcessReason.trim(),
      currentUser,
      `Pay period ending ${periodEnd(activePeriod)}`,
    );
    setUnlockProcessReason(null);
  };

  // Verify/Process buttons stay clickable even when blocked so we can explain
  // (via a warning popup) exactly what the admin must finish to move forward.
  const tryVerifyHours = () => {
    const reasons: string[] = [];
    if (!rulesComplete)
      reasons.push(
        "Commit all Payroll Rules first — holidays, unpaid breaks, tip rates, FOH roles and salaried employees.",
      );
    if (hoursOutstanding)
      reasons.push("Resolve the pending clock-in/out tasks on the manager side.");
    if (reasons.length) {
      setVerifyWarning({ title: "Can’t verify hours yet", reasons });
      return;
    }
    confirmHours();
  };

  const tryVerifyTips = () => {
    const reasons: string[] = [];
    if (!rulesComplete)
      reasons.push(
        "Commit all Payroll Rules first — holidays, unpaid breaks, tip rates, FOH roles and salaried employees.",
      );
    if (periodOutstanding)
      reasons.push(
        "Resolve all outstanding manager tasks — clock-in/out exceptions and Event party pools.",
      );
    if (reasons.length) {
      setVerifyWarning({ title: "Can’t verify tips yet", reasons });
      return;
    }
    verifyTips();
  };

  const tryProcess = () => {
    if (!tipsVerified) {
      setVerifyWarning({
        title: "Can’t process payroll yet",
        reasons: ["Verify Tips before processing payroll."],
      });
      return;
    }
    // Warn (but don't block) if the Staff Accounts report is missing.
    if (!staffReport) {
      setStaffWarnOpen(true);
      return;
    }
    processPayroll();
  };

  // Staff Accounts upload (metadata only in this mock; blob-backed in prod).
  const triggerStaffUpload = () => staffFileRef.current?.click();

  // "No activity this period" — close out the Overview stage with no report.
  const skipStaffAccounts = () => {
    if (!activePeriod || staffReport) return;
    payflowActions.skipStaffAccounts(
      activePeriod.id,
      currentUser,
      `Pay period ending ${periodEnd(activePeriod)}`,
      buildOverviewFreeze(),
    );
    setPeriodTab("payroll");
  };

  const handleStaffUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const period = activePeriod;
    e.target.value = ""; // allow re-selecting the same file
    if (!file || !period) return;
    let entries: { name: string; amount: number }[] = [];
    try {
      entries = await parseStaffAccountsPdf(file);
    } catch (err) {
      console.error("Failed to parse Staff Accounts PDF:", err);
    }
    payflowActions.setStaffAccountsReport(
      period.id,
      file.name,
      entries,
      currentUser,
      `Pay period ending ${periodEnd(period)}`,
    );
  };

  // Verify the ingested on-account amounts → locks them and advances to Summary.
  const verifyStaffAccounts = () => {
    if (!activePeriod || !staffReport || staffVerified) return;
    payflowActions.verifyStaffAccounts(
      activePeriod.id,
      currentUser,
      `Pay period ending ${periodEnd(activePeriod)}`,
      buildOverviewFreeze(),
    );
    setPeriodTab("payroll");
  };

  const unlockStaffAccounts = () => {
    if (!activePeriod || !staffVerified) return;
    // A skipped period ("no activity") has no report to edit, and its Overview stage was
    // never lifecycle-locked — so unverify would be a server-side noop and snap straight
    // back to locked. Clearing it instead returns to the Upload-or-Skip choice, which is
    // what "I need to change this" actually means for a skip.
    if (staffReport?.skipped) {
      payflowActions.clearStaffAccountsReport(
        activePeriod.id,
        currentUser,
        `Pay period ending ${periodEnd(activePeriod)}`,
      );
      return;
    }
    payflowActions.unverifyStaffAccounts(
      activePeriod.id,
      currentUser,
      `Pay period ending ${periodEnd(activePeriod)}`,
    );
  };

  // Save a Summary rate / tip-pool / staff-account edit.
  const saveSummaryEdit = () => {
    if (!summaryEdit) return;
    const value = parseFloat(summaryEdit.value);
    const num = Number.isFinite(value) ? value : 0;
    if (summaryEdit.field === "staffAccount") {
      if (activePeriod)
        payflowActions.setStaffAccountForName(
          activePeriod.id,
          summaryEdit.employee,
          num,
          currentUser,
        );
    } else {
      payflowActions.setSummaryOverride(
        summaryEdit.key,
        { [summaryEdit.field]: num },
        currentUser,
        `${summaryEdit.employee} · ${summaryEdit.label}`,
      );
    }
    setSummaryEdit(null);
  };

  const confirmUnlockTipsVerify = () => {
    if (!activePeriod || !unlockTipsVerifyReason || !unlockTipsVerifyReason.trim()) return;
    payflowActions.unlockTips(
      activePeriod.id,
      unlockTipsVerifyReason.trim(),
      currentUser,
      `Pay period ending ${periodEnd(activePeriod)}`,
    );
    setUnlockTipsVerifyReason(null);
  };

  // Flow-status timeline for the history modal.
  const periodTimeline = (p: PayPeriod) => {
    const end = ymdFromISO(p.endISO);
    const created = new Date(end.year, end.monthIndex, end.day + 1); // day after period end
    const pending = periodHasPending(p, adjustments, reviews);
    const hv = hoursApproved[p.id];
    const tv = tipsApproved[p.id];
    const sa = staffAccounts[p.id];
    const processed = p.stage === "processed" || !!processedPeriods[p.id];
    return [
      {
        name: pending ? "Pending" : "Ready",
        done: true,
        by: "System",
        at: created.toLocaleDateString(),
      },
      {
        name: "Hours Verified",
        done: !!hv,
        by: hv?.by ?? "",
        at: hv ? new Date(hv.at).toLocaleString() : "",
      },
      {
        name: "Tips Verified",
        done: !!tv,
        by: tv?.by ?? "",
        at: tv ? new Date(tv.at).toLocaleString() : "",
      },
      {
        name: sa?.skipped ? "Staff Accounts — No Activity" : "Staff Accounts Uploaded",
        done: !!sa?.fileName || !!sa?.skipped,
        by: sa?.fileName || sa?.skipped ? sa.uploadedBy : "",
        at: sa?.fileName || sa?.skipped ? new Date(sa.uploadedAt).toLocaleString() : "",
      },
      {
        name: "Overview Verified",
        done: !!sa?.verified,
        by: sa?.verifiedBy ?? "",
        at: sa?.verifiedAt ? new Date(sa.verifiedAt).toLocaleString() : "",
      },
      {
        name: "Payroll Processed",
        done: processed,
        by: processed ? "—" : "",
        at: processed ? p.statusDate : "",
      },
    ];
  };

  // Opening the screen pulls the payrolls and rules fresh.
  useEffect(() => {
    void refreshFromServer();
  }, []);

  // A new build always shows its progress; hiding only applies to that build.
  useEffect(() => {
    setBuildHidden(false);
  }, [building]);

  // The inner tab a period should open to, driven by how far it has progressed:
  //   processed / tips verified → Summary
  //   hours verified            → Tips
  //   otherwise                 → Hours
  const defaultTabFor = (id: string): "hours" | "tips" | "summary" | "payroll" => {
    const p = payPeriods.find((pp) => pp.id === id);
    if (p?.stage === "processed" || processedPeriods[id]) return "summary";
    if (tipsApproved[id]) return "summary";
    if (hoursApproved[id]) return "tips";
    return "hours";
  };

  const openPeriod = (id: string) => {
    setView(`period:${id}`);
    setSearch("");
    setPeriodTab(defaultTabFor(id));
  };

  const activePeriodId = view.startsWith("period:")
    ? view.slice("period:".length)
    : null;
  const activePeriod =
    activePeriodId != null ? payPeriods.find((p) => p.id === activePeriodId) : undefined;
  const activeYear = view.startsWith("year:") ? Number(view.slice("year:".length)) : null;

  /* ── The rules shadow ───────────────────────────────────────────────────
     Every figure on a pay period is computed from the rules THAT PERIOD WAS
     BUILT UNDER, not from the rules as they stand today. Editing a tip rate
     must change the next payroll, never a finished one.

     This is done by rebinding the unprefixed names — `tipRates`, `breakRules`,
     `fohRoles`, `payConstants`, `committedHolidays` — over the live ones for the
     rest of the component. Every math site below already closes over exactly
     these names, so they change source without a single edit to the arithmetic.
     The live values remain reachable as `live*` for the Payroll Rules tab, which
     is the one place that must show and edit what is current.

     A payroll built before rules were snapshotted has none, and falls back to the
     live rules — the old behaviour, and the screen says so rather than pretending
     it knows. */
  const periodRules: PayflowRules = activePeriod?.rules ?? {
    tipRates: liveTipRates,
    tipRatesLock: liveTipRatesLock,
    breakRules: liveBreakRules,
    breakRulesLock: liveBreakRulesLock,
    fohRoles: liveFohRoles,
    fohRolesLock: liveFohRolesLock,
    salariedEmployeeIds: liveSalariedEmployeeIds,
    salariedLock: liveSalariedLock,
    committedHolidays: liveCommittedHolidays,
    payConstants: livePayConstants,
    payPeriod,
  };
  const { tipRates, breakRules, fohRoles, payConstants, committedHolidays } = periodRules;
  // Whether an employee is salaried for the OPEN period — reads that period's snapshot
  // (fall back to [] for a payroll built before salaried employees existed).
  const salariedIds = periodRules.salariedEmployeeIds ?? [];
  const isSalaried = (id: string) => salariedIds.includes(id);
  // True when the open payroll predates rule snapshotting, so its figures are being
  // computed from today's rules and can still move underneath it.
  const rulesUnsnapshotted = !!activePeriod && !activePeriod.rules;

  // FOH eligibility for the Summary/Payroll premium — reads the period's snapshot,
  // which is why it lives here rather than beside `availableRoles`.
  const roleIsFoh = (role: string) => fohRoles.includes(role);

  // Verification state for the open period (locks Net Hours / tips when set).
  // Verify Hours only needs the exception (clock-in/out) tasks resolved — pool
  // confirmations are a tips concern and must not block hours.
  const hoursOutstanding = activePeriod
    ? periodHasPending(activePeriod, adjustments, reviews)
    : false;
  // Verify Tips needs everything resolved: exceptions AND unconfirmed pools.
  const periodOutstanding = activePeriod
    ? periodHasOutstandingTasks(activePeriod, adjustments, reviews, manualPools, barAllocations)
    : false;
  const periodApproved = activePeriod ? !!hoursApproved[activePeriod.id] : false; // hours verified
  // Once Hours is verified, the Hours tab renders the figures frozen at that moment, not a
  // recomputation — so a later change to the hours engine can't rewrite a verified period.
  // Undefined until a period is approved (or for one verified before freezing existed, in
  // which case the tab falls back to the live computation per row).
  const frozenHours =
    activePeriod && periodApproved ? frozenStages[activePeriod.id]?.hours : undefined;
  const tipsVerified = activePeriod ? !!tipsApproved[activePeriod.id] : false;
  const periodProcessed = activePeriod
    ? activePeriod.stage === "processed" || !!processedPeriods[activePeriod.id]
    : false;
  // Staff Accounts report uploaded for the open period (manual, once per period).
  const staffReport = activePeriod ? staffAccounts[activePeriod.id] : undefined;
  const staffVerified = !!staffReport?.verified;
  // A committed (rules-list) stat holiday falling inside this period drives the
  // Summary Stat columns — hidden entirely when no stat day applies.
  const statHolidayISO = activePeriod
    ? activePeriod.days.find((d) => isStatHoliday(d.dateISO, committedHolidays))?.dateISO
    : undefined;
  const periodHasStat = !!statHolidayISO;
  // All payroll rules must be committed before any verification can proceed:
  // unpaid-break rules, tip rates, FOH roles, and the holiday list for the
  // period's year.
  //
  // Read from the period's OWN snapshot, so the question is "were the rules committed
  // when this payroll was built" — the same rules its figures were computed from —
  // rather than "are they committed right now". Committing a rule today therefore
  // does not retrospectively bless a payroll that was built from a draft; that
  // payroll has to be rebuilt, which is the only thing that would actually make its
  // numbers reflect the committed rule.
  const rulesComplete =
    !!periodRules.breakRulesLock &&
    !!periodRules.tipRatesLock &&
    !!periodRules.fohRolesLock &&
    !!periodRules.salariedLock &&
    !!activePeriod &&
    !!committedHolidays[String(periodYear(activePeriod))];

  // The Hours tab's computed output for every row, captured to freeze at Verify Hours.
  // Keyed exactly as the tab reads it back (the full rowKey) and computed under the
  // period's own break rules — the same inputs the live tab uses, so the frozen copy is
  // byte-for-byte what was on screen when it was verified.
  const buildHoursFreeze = (): Record<string, RowComputation> => {
    const out: Record<string, RowComputation> = {};
    if (!activePeriod) return out;
    for (const day of activePeriod.days) {
      for (const r of day.rows) {
        const key = rowKey(activePeriod.id, day.index, r.id, r.seq);
        out[key] = computeRow(r, adjustments[key], reviews[key], breakRules);
      }
    }
    return out;
  };

  const confirmHours = () => {
    if (!activePeriod || hoursOutstanding || periodApproved) return;
    payflowActions.approveHours(
      activePeriod.id,
      currentUser,
      `Pay period ending ${periodEnd(activePeriod)}`,
      buildHoursFreeze(),
    );
    // Verifying hours advances the workflow straight to the Tips screen.
    setPeriodTab("tips");
  };

  const confirmUnlockHours = () => {
    if (!activePeriod || !unlockHoursReason || !unlockHoursReason.trim()) return;
    payflowActions.unlockHours(
      activePeriod.id,
      unlockHoursReason.trim(),
      currentUser,
      `Pay period ending ${periodEnd(activePeriod)}`,
    );
    setUnlockHoursReason(null);
  };

  // Verifications must be unlocked in reverse order — an upstream stage can't be
  // reopened while a downstream stage that depends on it is still locked (Tips
  // depend on Hours; the payroll depends on Tips), because the earlier change
  // could invalidate the later result. Point the admin at the step to unlock
  // first instead of allowing an inconsistent state.
  const UNLOCK_STAGES = ["Hours", "Tips", "Overview", "Payroll"];
  const tryUnlockStage = (stage: number, openUnlock: () => void) => {
    const done = [periodApproved, tipsVerified, staffVerified, periodProcessed];
    for (let j = stage + 1; j < done.length; j++) {
      if (done[j]) {
        setVerifyWarning({
          title: `Can’t unlock ${UNLOCK_STAGES[stage]} yet`,
          reasons: [
            j === 3
              ? "You need to un-process the payroll first."
              : `You need to unlock ${UNLOCK_STAGES[j]} first.`,
          ],
        });
        return;
      }
    }
    openUnlock();
  };

  const filteredDays = useMemo(() => {
    if (!activePeriod) return [];
    const term = search.trim().toLowerCase();
    return activePeriod.days
      .map((day) => ({
        ...day,
        rows: term
          ? day.rows.filter(
              (r) =>
                r.name.toLowerCase().includes(term) ||
                r.role.toLowerCase().includes(term),
            )
          : day.rows,
      }))
      .filter((day) => day.rows.length > 0);
  }, [activePeriod, search]);

  // Summary report: one line per employee+role with cumulative net hours for
  // the whole period, sorted alphabetically by last name. An employee who
  // worked more than one role (e.g. Server and Bartender) gets a line each.
  const summaryRows = useMemo(() => {
    if (!activePeriod) return [];
    const map = new Map<string, { id: string; name: string; role: string; netHours: number }>();
    for (const day of activePeriod.days) {
      for (const r of day.rows) {
        // Event accounts are party pools and Takeout is a generic sales bucket —
        // neither is an employee, so neither is ever a Summary line.
        if (isHiddenAccount(r.name)) continue;
        const key = rowKey(activePeriod.id, day.index, r.id, r.seq);
        const nh = computeRow(r, adjustments[key], reviews[key], breakRules).netHours;
        const hours = Number.isFinite(nh) ? nh : 0;
        const mapKey = `${r.id}__${r.role}`;
        const cur = map.get(mapKey);
        if (cur) cur.netHours += hours;
        else map.set(mapKey, { id: r.id, name: r.name, role: r.role, netHours: hours });
      }
    }
    const lastName = (n: string) =>
      n.trim().split(/\s+/).slice(-1)[0]?.toLowerCase() ?? "";
    return [...map.values()].sort(
      (a, b) =>
        lastName(a.name).localeCompare(lastName(b.name)) ||
        a.name.localeCompare(b.name) ||
        a.role.localeCompare(b.role),
    );
  }, [activePeriod, adjustments, reviews, breakRules]);

  // A person's on-account balance is per PERSON, not per role. Someone who
  // worked as bartender, server and host has three Summary lines, and the staff
  // account must land on exactly ONE of them or it is deducted once per role
  // (which is what forced the "divide by three" workaround). This picks the
  // first line in Summary order as the one that carries it; the rest show none.
  const staffAcctRoleByPerson = useMemo(() => {
    const first = new Map<string, string>();
    for (const s of summaryRows) if (!first.has(s.id)) first.set(s.id, s.role);
    return first;
  }, [summaryRows]);

  const sortedPeriods = useMemo(
    () => [...payPeriods].sort((a, b) => periodSortKey(b) - periodSortKey(a)),
    [payPeriods],
  );

  // A period belongs to the year of its end date. Year tabs archive every
  // period; the Overview shows only the 6 most recent.
  const OVERVIEW_LIMIT = 6;
  const years = useMemo(
    () =>
      [...new Set(payPeriods.map(periodYear))].sort((a, b) => b - a),
    [payPeriods],
  );
  const recentPeriods = sortedPeriods.slice(0, OVERVIEW_LIMIT);
  const yearPeriods =
    activeYear != null ? sortedPeriods.filter((p) => periodYear(p) === activeYear) : [];

  const addPeriodOptions = useMemo(
    () =>
      fortnightlyEndOptions(
        payPeriod.anchorEnd,
        payPeriod.lengthDays,
        new Set(payPeriods.map((p) => p.endISO)),
      ),
    [payPeriods, payPeriod],
  );

  // The open period's rows come from the detail endpoint, so pull them the
  // first time it's opened. The period before it is pulled too — stat pay looks
  // back four weeks, which reaches into the previous payroll.
  useEffect(() => {
    if (!activePeriodId) return;
    const index = sortedPeriods.findIndex((p) => p.id === activePeriodId);
    if (index < 0) return;
    void payflowActions.loadPeriodDetail(activePeriodId);
    const previous = sortedPeriods[index + 1];
    if (previous) void payflowActions.loadPeriodDetail(previous.id);
  }, [activePeriodId, sortedPeriods]);

  // A processed period's Summary tab renders from the stored register (what was paid),
  // not a recomputation — so load it when such a period is open. Loaded only once per
  // period (the action no-ops when it already holds it, or knows there is none).
  useEffect(() => {
    if (activePeriodId && periodProcessed) void payflowActions.loadRegister(activePeriodId);
  }, [activePeriodId, periodProcessed]);

  // The FOH-role picker and the tip sheet read the roles seen in the schedule,
  // so keep the newest payroll's detail loaded even on the Overview / Rules tabs.
  useEffect(() => {
    const newest = sortedPeriods[0];
    if (newest) void payflowActions.loadPeriodDetail(newest.id);
  }, [sortedPeriods]);

  // The Overview list derives each period's status from the lifecycle locks, which live
  // in the per-period state doc and only reach the global maps once that period has been
  // loaded. Without this, a period nobody has opened yet shows no locks and falls back to
  // an early stage — a processed payroll reads as "Process hours" until it is clicked,
  // then corrects on return. So load every period's days AND decisions up front, exactly
  // as the manager screen does. Both halves decline the round trip when they already hold
  // what they would fetch, so this settles to a no-op once everything is in hand. It also
  // gives stat pay the full prior window (the holiday reaches back across two fortnights),
  // rather than only the single previous period the open-period effect loads.
  useEffect(() => {
    void payflowActions.loadAllPeriodDetails();
  }, [payPeriods]);

  // How current the data is drives a line on the overview, so it is read on load
  // rather than only when the pull modal is opened. If a pull is already running —
  // started by the nightly schedule or another admin — this picks up watching it.
  useEffect(() => {
    void payflowActions.loadDataPull();
  }, []);

  // Close the modal outright when a pull this tab started finishes, rather than letting
  // it fall back to the calendar. The calendar is for choosing a window to pull; once
  // that window has been pulled there is nothing left to choose there, and leaving it up
  // reads as though the pull still needs asking for.
  //
  // This is an EDGE, not a condition. "The modal is open and no pull is running" is also
  // true the moment the user opens it, so a plain condition would close it before they
  // could press anything. What closes it is the transition out of watching, which is why
  // the previous value has to be remembered.
  //
  // Keyed on `dataPullWatch` and not on `pullInFlight`, because only a pull THIS tab
  // asked for ever sets a watch — reconcileWatch adopts someone else's run through
  // `dataPull` alone and leaves the watch null. So the nightly run and another admin's
  // pull can finish while this modal is open and it will stay open, which is right: the
  // user opened it to look, not because they were waiting on something.
  useEffect(() => {
    const stoppedWatching = wasWatchingOwnPull.current && dataPullWatch === null;
    // Updated on every run, and before any early return, or the edge is only ever seen
    // once and the second pull of a session leaves the modal open.
    wasWatchingOwnPull.current = dataPullWatch !== null;
    if (!stoppedWatching) return;
    // A watch also ends when the run was superseded, when its lease lapsed, and when the
    // server stayed unable to read ReconState long enough to give up. Those set a
    // problem, and closing the modal would take the explanation off the screen with it.
    if (dataPullError) return;
    // `partial` and `failed` are finished, but the note at the foot of this modal is the
    // only place either is reported — and a partial pull is exactly the case where the
    // data is half there and the next payroll built from it will be wrong. So only a
    // clean run closes; the rest stay up to be read.
    if (dataPull?.state !== "succeeded") return;
    // Opened to fill a coverage gap: the data is now in, so build the payroll
    // that was refused before — the admin doesn't have to click Add Pay Period
    // again. Only a clean pull reaches here, so it never builds on half the data.
    if (pendingBuildEnd) {
      const toBuild = pendingBuildEnd;
      setPendingBuildEnd(null);
      void payflowActions.createPayroll(toBuild, false);
    }
    setPullOpen(false);
  }, [dataPullWatch, dataPull, dataPullError, pendingBuildEnd]);

  // `option.exists` comes from the payroll list this tab loaded, which can be minutes
  // old and does not see another admin's work — so the server is asked directly before
  // anything is created. Getting this wrong overwrites a finished payroll without
  // asking, which is the one mistake on this screen that destroys work.
  const startBuild = async (option: PeriodOption) => {
    setAddPeriodOpen(false);
    let exists = option.exists;
    try {
      exists = await payflowActions.payrollExists(option.iso);
    } catch {
      // The check itself failed. Fall back to what the list says rather than blocking
      // the create — the server refuses a duplicate with a 409 regardless, so the
      // confirmation is a courtesy and the guarantee is on the server.
    }
    if (exists) {
      setReplaceTarget(option);
      return;
    }
    void payflowActions.createPayroll(option.iso, false);
  };

  const confirmReplacePeriod = () => {
    if (!replaceTarget) return;
    const { iso } = replaceTarget;
    setReplaceTarget(null);
    void payflowActions.createPayroll(iso, true);
  };

  const confirmDeletePeriod = () => {
    if (!deletePeriodTarget) return;
    void payflowActions.deletePeriod(
      deletePeriodTarget.id,
      currentUser,
      `Pay period ending ${periodEnd(deletePeriodTarget)}`,
    );
    setDeletePeriodTarget(null);
  };

  const renderPeriodList = (title: string, periods: PayPeriod[], onAdd?: () => void) => (
    <div className="payflow-card">
      <div className="payflow-card-header">
        {/* Overview: the table's own "Pay Period" column header makes a separate
            "Pay Periods" title redundant, so the Add button takes its place. The
            year archive keeps its title (it carries the year). */}
        {onAdd ? (
          <button className="payflow-add-period" title="Add pay period" onClick={onAdd}>
            Add Pay Period
          </button>
        ) : (
          <div className="payflow-card-title">{title}</div>
        )}
      </div>
      <div className="payflow-ov-table">
        <div className="payflow-ov-header">
          <div>Pay Period</div>
          <div>Status</div>
          <div>Date</div>
          <div className="payflow-ov-actions-col">Actions</div>
        </div>
        {!hydrated ? (
          <div className="payflow-loading">
            <span className="payflow-spinner" />
            <span>Loading payrolls…</span>
          </div>
        ) : periods.length === 0 ? (
          <div className="payflow-control-empty">No pay periods.</div>
        ) : (
          periods.map((p) => {
            const status = periodStatus(
              p,
              adjustments,
              reviews,
              hoursApproved,
              tipsApproved,
              processedPeriods,
              manualPools,
              barAllocations,
            );
            return (
              <div key={p.id} className="payflow-ov-row">
                <div className="payflow-ov-name">Payroll ending {periodEnd(p)}</div>
                <div>
                  <span className={`payflow-ov-status payflow-ov-status-${status}`}>
                    {STATUS_LABEL[status]}
                  </span>
                </div>
                <div className="payflow-ov-date">{p.statusDate}</div>
                <div className="payflow-ov-actions-col">
                  <button
                    className="payflow-ov-view"
                    title="View pay period"
                    onClick={() => openPeriod(p.id)}
                  >
                    👁
                  </button>
                  <button
                    className="payflow-ov-history"
                    title="Status history"
                    onClick={() => setHistoryPeriod(p)}
                  >
                    ↻
                  </button>
                  {/* Refresh (source data) — a global action, in the row action bar for
                      platform-consistent styling. Overview only, never the year archive. */}
                  {onAdd && (
                    <button
                      className="payflow-ov-refresh"
                      title={pullRunning ? "Refreshing source data…" : "Refresh source data"}
                      aria-label={pullRunning ? "Refreshing source data" : "Refresh source data"}
                      onClick={() => setPullOpen(true)}
                    >
                      {pullRunning ? (
                        <span className="payflow-spinner payflow-spinner-inline" />
                      ) : (
                        <PipelineIcon />
                      )}
                    </button>
                  )}
                  <button
                    className="payflow-ov-delete"
                    title="Delete pay period"
                    onClick={() => setDeletePeriodTarget(p)}
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  // Tip-out continuation: keeps Employee / Role / Net Hours (locked) and swaps
  // the timesheet columns for the tip-out columns. Sales are dummy POS data.
  const showTipouts = periodTab === "tips";

  // Per-day tip-out computation:
  //   • deductions for servers (Pool/CC/Host/Bar) and bartenders (Pool/CC/Host/BB)
  //   • subtotal = CC Tips − deductions
  //   • Host & BarBack pools split among their members by hours
  //   • Bar pool (from servers) routes into the bartender pool of the same window
  //   • Bartenders in a pool split evenly; servers stay individual
  const isHostRole = (role: string) => /host|food.?runner/i.test(role);
  const isBarbackRole = (role: string) => /barback/i.test(role);
  // Supervisors carry a "(SV)" tag on their role (e.g. "Bartender (SV)") so the FOH
  // premium can target them separately — but for tip/pool behaviour they are just
  // their base role. Strip the tag when asking "is this a bartender/server shift".
  const baseRole = (role: string) => role.replace(/\s*\(SV\)\s*$/i, "");
  // A bartending shift for pooling purposes: an explicit Bartender, OR anyone the
  // scheduler assigned a bar location to. A Manager on Duty who also works the bar
  // gets a location (e.g. "Main Bar") on their shift — that is the signal they are
  // bartending, so they join and share that bar's pool.
  // A bartender, or a non-server given a bar location (e.g. a manager working the bar). A
  // SERVER keeps its role even when assigned a bar location (Parlour/Patio) — servers can be
  // location-assigned without being treated as bartenders.
  const isBarShift = (r: { role: string; location?: string }) =>
    baseRole(r.role) === "Bartender" || (!!r.location && baseRole(r.role) !== "Server");

  // One staff member's cut of an Event party pool, shown as a full tipout child row under
  // the party. `group` is the manager's group name (Bar / Server / Kitchen, or Pool 1/2/3)
  // and shows in the Role column. The CC-tips vs auto-grat split is carried through to the
  // net because staff are taxed on auto-grats but not on direct (patron) tips.
  interface PartyChild {
    memberId: string;
    name: string;
    group: string;
    // Net hours shown ONLY when the member has no own-sales line that day (kitchen/host/
    // runners); 0 when they have their own line (hours show there, avoiding double display).
    netHours: number;
    ccTipsShare: number; // this member's share of the party's CC (direct) tips
    autoShare: number; // this member's share of the party's auto-grats
    poolCut: number; // tip-pool tipout on their share
    ccFee: number; // credit-card fee on their share
    tipsOwed: number; // net direct tips (not taxed)
    autoOwed: number; // net auto-grats (taxed)
  }

  const computeDayTips = (day: Day) =>
    (() => {
      const solo = poolSolo[`${activePeriod!.id}:${day.index}`] || [];
      // A shift belongs to the window where MORE of its hours fall, not where it
      // starts — so a bartender who clocks in at 4:55 to work the night pools with
      // the night, not the day. Tie → night (the busier side). This single-pool
      // placement is what lets the bar pool split evenly without a shift that
      // straddles 5 PM drawing a full share from two pools at once.
      const BOUNDARY = 17 * 60; // 5 PM, minutes from midnight
      const winOf = (span: { start: number; end: number }): "day" | "night" => {
        // No usable span (orphan sales the pipeline couldn't match to a shift):
        // keep the day window it has always been given.
        if (span.end <= span.start) return "day";
        const dayMins = Math.max(0, Math.min(span.end, BOUNDARY) - span.start);
        const nightMins = Math.max(0, span.end - Math.max(span.start, BOUNDARY));
        return nightMins >= dayMins ? "night" : "day";
      };
      // A row with neither a schedule nor a clock record has no window of its own.
      const windowOf = (rr: Day["rows"][number]) => poolWindowSpan(rr) ?? { start: 0, end: 0 };

      const makeEntry = (
        rr: Day["rows"][number],
        t: Tipout,
        netHours: number,
      ) => {
        // The window most of the shift falls in — by the schedule when there is
        // one, else by the hours actually worked.
        const win = winOf(windowOf(rr));
        return {
          r: rr,
          t,
          netHours: Number.isFinite(netHours) ? netHours : 0,
          win,
          // solo'd members are pulled out of pooling (e.g. lone Parlour bar). An Event
          // party account is always solo: it is shown on the Tips tab for audit (its CC
          // fee + tipouts) but never merges into a real pool — its money is distributed to
          // staff through the manager's party groups, not the automatic bar pool.
          pool:
            solo.includes(rr.id) || isEventAccount(rr.name)
              ? null
              : isBarShift(rr)
                ? (`bar-${win}` as PoolKey)
                : poolKeyForWindow(rr.role, win),
          tipPool: 0,
          ccFee: 0,
          host: 0,
          barBack: 0,
          bar: 0,
          subtotal: 0, // CC tips − own deductions
          tips: 0, // final tips owed
          auto: 0,
          // Party-pool fields. `partyTipsShare`/`partyAutoShare` are this member's NET cut of
          // any Event party that day, kept split (direct vs auto) for tax and carried
          // separately from their own tips so they reach pay (periodTipTotals) WITHOUT showing
          // on the member's own tip line. On an Event account row, `partyChildren` holds the
          // per-member split for the expander and `partyHidden` marks a second Event row of
          // the same account (figures folded into the first).
          partyTipsShare: 0,
          partyAutoShare: 0,
          partyChildren: [] as PartyChild[],
          partyHidden: false,
          // A party member with no own tip line (kitchen/host/runner, no own sales): their
          // whole presence on the Tips tab is the party child, so their empty normal row is
          // suppressed to avoid showing their hours twice.
          partyOnlyHidden: false,
        };
      };

      // Event party accounts ARE shown on the Tips tab (as solo rows — see makeEntry) so
      // the accountant can see the party paid its CC fee and tipouts, but they contribute
      // nothing to the real staff pools and are excluded from pay/Hours/Summary. Their
      // auto-grats are the party pool, distributed to staff by the manager's group % below.
      const base = day.rows.flatMap((r) => {
        // Takeout is a generic sales account, not a person, and takeout orders
        // don't tip — so it never enters the tip engine: no pool, no tip-out, no
        // pay line, and nothing on Tips/Summary/Overview, which all derive from
        // here.
        if (isTakeoutAccount(r.name)) return [];
        const key = rowKey(activePeriod!.id, day.index, r.id, r.seq);
        // Real POS figures where the pipeline found a sales record; zero otherwise.
        const t = tipoutFromSales(r.sales ?? EMPTY_SHIFT_SALES);
        const nh = computeRow(r, adjustments[key], reviews[key], breakRules).netHours;
        const netHours = Number.isFinite(nh) ? nh : 0;
        // One shift, one pool: the whole shift (and its sales) goes to the window
        // most of its hours fall in (winOf). No day/night tranche split — that
        // existed to share a long bar shift across both pools by hours, which only
        // made sense while pools were hours-weighted; with an even split it would
        // let a shift straddling 5 PM draw a full share from two pools at once.
        return [makeEntry(r, t, netHours)];
      });

      // Presence per window — a tip-out is only charged if its recipient pool
      // has someone in the SAME window (else the contribution would vanish). Event party
      // accounts never count as "present": a lone party account must not make real
      // servers think a bar was staffed, etc.
      const presentIn = (pred: (role: string) => boolean, w: "day" | "night") =>
        base.some((b) => !isEventAccount(b.r.name) && pred(b.r.role) && b.win === w);
      const hostPresent = { day: presentIn(isHostRole, "day"), night: presentIn(isHostRole, "night") };
      const barbackPresent = {
        day: presentIn(isBarbackRole, "day"),
        night: presentIn(isBarbackRole, "night"),
      };
      const barPresent = {
        day: base.some((b) => !isEventAccount(b.r.name) && isBarShift(b.r) && b.win === "day"),
        night: base.some((b) => !isEventAccount(b.r.name) && isBarShift(b.r) && b.win === "night"),
      };

      // Pools are split by window (day/night); contributions tagged by the
      // contributor's own window so day tip-outs feed the day pools, etc.
      const hostPool: Record<string, number> = { day: 0, night: 0 };
      const barbackPool: Record<string, number> = { day: 0, night: 0 };
      const barPool: Record<string, number> = { day: 0, night: 0 };

      // Server-to-bar money all goes to the Main Bar pool. A bartender allocated to Main Bar
      // (by role/location, regardless of their own sales) receives it. When no bartender is
      // on Main Bar, a manager allocates it (see barIssuesInPeriod) — `allocFor(win)` holds
      // their chosen recipient ids; an empty `recipients` list means nobody, so the charge is
      // waived and the servers keep it. No allocation yet = an unresolved task blocking verify.
      const barAlloc = barAllocations[activePeriod!.id] ?? {};
      const onMainBar = (r: { location?: string }) => (r.location || "Main Bar") === "Main Bar";
      const mainBarPresent = {
        day: base.some(
          (b) =>
            !isEventAccount(b.r.name) && isBarShift(b.r) && b.win === "day" && onMainBar(b.r),
        ),
        night: base.some(
          (b) =>
            !isEventAccount(b.r.name) && isBarShift(b.r) && b.win === "night" && onMainBar(b.r),
        ),
      };
      const allocFor = (w: "day" | "night") => barAlloc[`${day.dateISO}|${w}`];
      // Servers pay no tip-out for a window the manager allocated to nobody (empty recipients).
      const waiveServerToBar = (w: "day" | "night") => {
        if (mainBarPresent[w]) return false;
        const a = allocFor(w);
        return !!a && a.recipients.length === 0;
      };

      base.forEach((b) => {
        const isServer = baseRole(b.r.role) === "Server";
        const isBartender = isBarShift(b.r);
        if ((isServer || isBartender) && b.t.hasSales) {
          b.tipPool = (b.t.netSales * tipRates.tipPool) / 100;
          // CC fee applies to ALL tips paid on credit card — CC tips AND auto-grats (auto-
          // grats are assumed CC when we can't tell).
          b.ccFee = ((b.t.ccTips + b.t.autoGrat) * tipRates.creditCardFee) / 100;
          b.host = hostPresent[b.win] ? (b.t.netSales * tipRates.hostFoodRunners) / 100 : 0;
          b.barBack =
            isBartender && barbackPresent[b.win]
              ? (b.t.netSales * tipRates.barBackPool) / 100
              : 0;
          // Not charged when the manager allocated this window's server-to-bar to nobody
          // (no Main Bar bartender, and they chose no recipient) — the servers keep it.
          b.bar =
            isServer && barPresent[b.win] && !waiveServerToBar(b.win)
              ? (b.t.netSales * tipRates.serverToBar) / 100
              : 0;
          // An Event party account shows these deductions on its own row (audit: it paid
          // its CC fee + tipouts) but does NOT feed the real staff pools — the party's
          // money reaches staff through the manager's group split of the auto-grats.
          if (!isEventAccount(b.r.name)) {
            hostPool[b.win] += b.host;
            barbackPool[b.win] += b.barBack;
            if (isServer) barPool[b.win] += b.bar;
          }
          // Deductions are apportioned across the CC-tips (direct, untaxed) and auto-grat
          // (taxed "controlled tips") buckets in proportion to each, so the taxable auto is
          // reduced by its fair share — staff aren't taxed on money they had to tip out.
          const totalTipouts = b.tipPool + b.ccFee + b.host + b.barBack + b.bar;
          const gross = b.t.ccTips + b.t.autoGrat;
          b.subtotal = gross > 0 ? b.t.ccTips - totalTipouts * (b.t.ccTips / gross) : 0;
          b.auto = gross > 0 ? b.t.autoGrat - totalTipouts * (b.t.autoGrat / gross) : 0;
        }
      });

      // Distribute host & barback pools to their window's members, by hours.
      (["day", "night"] as const).forEach((w) => {
        const hostMembers = base.filter((b) => isHostRole(b.r.role) && b.win === w);
        const bbMembers = base.filter((b) => isBarbackRole(b.r.role) && b.win === w);
        const hostHours = hostMembers.reduce((s, b) => s + b.netHours, 0);
        const bbHours = bbMembers.reduce((s, b) => s + b.netHours, 0);
        hostMembers.forEach((b) => {
          b.tips +=
            hostHours > 0 ? hostPool[w] * (b.netHours / hostHours) : hostPool[w] / hostMembers.length;
        });
        bbMembers.forEach((b) => {
          b.tips +=
            bbHours > 0 ? barbackPool[w] * (b.netHours / bbHours) : barbackPool[w] / bbMembers.length;
        });
      });

      // Bartenders pool their OWN sales by bar location + window — each bar is its own pool
      // (e.g. Main Bar and Parlour split separately). The server→bar tip-out is NOT added
      // here; it is distributed to the Main Bar bartender(s) below.
      const barGroups = new Map<string, typeof base>();
      base.forEach((b) => {
        if (isBarShift(b.r) && b.pool !== null && b.t.hasSales) {
          const loc = b.r.location || "Main Bar";
          const gk = `${loc}|${b.win}`;
          if (!barGroups.has(gk)) barGroups.set(gk, []);
          barGroups.get(gk)!.push(b);
        }
      });
      barGroups.forEach((members) => {
        // Split EVENLY — the bartenders' own decision: equal share per person, not
        // by hours (their reasoning: the short-shift reliever often rang the higher
        // sales, so their contribution to the pool was already the larger). Safe to
        // split evenly because each shift now sits in exactly one pool by majority
        // hours (see winOf) — nobody straddling 5 PM draws an equal share from two.
        const share = members.reduce((s, b) => s + b.subtotal, 0) / members.length;
        const autoShare = members.reduce((s, b) => s + b.auto, 0) / members.length;
        members.forEach((b) => {
          b.tips = share;
          b.auto = autoShare;
        });
      });

      // Servers — and any solo'd bartenders — keep their own subtotal.
      base.forEach((b) => {
        if (b.t.hasSales && (baseRole(b.r.role) === "Server" || (isBarShift(b.r) && b.pool === null)))
          b.tips = b.subtotal;
      });

      // Server→bar money all goes to the Main Bar bartender(s), BY ALLOCATION — a Main Bar
      // bartender shares it even with no sales of their own (one → the whole sum, two+ →
      // split evenly, added on TOP of their own-sales share, which is why this runs last).
      // When no one is on Main Bar it goes to the recipients the manager allocated; an empty
      // allocation was already waived (not charged), and an unresolved one is received by no
      // one — the task blocks Verify Tips, so a verified/processed period never has this gap.
      const uniqueByPerson = (entries: typeof base): typeof base => {
        const seen = new Set<string>();
        return entries.filter((b) => {
          const pid = b.r.id.split("~")[0];
          if (seen.has(pid)) return false;
          seen.add(pid);
          return true;
        });
      };
      (["day", "night"] as const).forEach((w) => {
        if (barPool[w] <= 0) return;
        let recipients = base.filter(
          (b) => !isEventAccount(b.r.name) && isBarShift(b.r) && b.win === w && onMainBar(b.r),
        );
        if (recipients.length === 0) {
          const a = allocFor(w);
          if (a && a.recipients.length > 0) {
            const ids = new Set(a.recipients);
            recipients = base.filter(
              (b) => !isEventAccount(b.r.name) && ids.has(b.r.id.split("~")[0]),
            );
          }
        }
        const unique = uniqueByPerson(recipients);
        if (unique.length === 0) return; // waived or unresolved — nobody receives it here
        const share = barPool[w] / unique.length;
        unique.forEach((b) => {
          b.tips += share;
        });
      });

      // ── Party pools ──
      // Each Event account is one party. Its CC tips and auto-grats are split by the
      // manager's group percentages (evenly per member), then pool + CC-fee tipouts come off
      // per member — the CC-tips vs auto-grat split is kept because staff are taxed on
      // auto-grats but not on direct patron tips. Each member's net is shown ONLY under the
      // Event row's expander (as PartyChildren) and carried to pay via `partyTipsShare` /
      // `partyAutoShare` — never on the member's own tip line, so a member with their own
      // sales sees their own row AND their party cut, kept separate.
      const partyState = manualPools[activePeriod!.id]?.parties ?? {};

      // Event-account entries for the day, grouped by account. The first entry per account
      // becomes the display/expander row; any others are folded in and hidden.
      const eventEntriesByAccount = new Map<string, (typeof base)[number][]>();
      for (const b of base) {
        const ev = eventAccountName(b.r.name);
        if (!ev) continue;
        const list = eventEntriesByAccount.get(ev) ?? [];
        list.push(b);
        eventEntriesByAccount.set(ev, list);
      }

      // Does a member have their own tip line today — own sales, or a host/barback pool
      // seat? If so their hours show on that line and the party child shows 0 hours; if not
      // (kitchen/host/runner with nothing of their own) the child carries their hours, and
      // their otherwise-empty normal row is suppressed (partyOnlyHidden) so hours show once.
      const ownLineMemberIds = new Set(
        base
          .filter(
            (b) =>
              !isEventAccount(b.r.name) &&
              (b.t.hasSales || isHostRole(b.r.role) || isBarbackRole(b.r.role)),
          )
          .map((b) => b.r.id.split("~")[0]),
      );
      const netHoursByMember = new Map<string, number>();
      for (const b of base) {
        if (isEventAccount(b.r.name)) continue;
        const mid = b.r.id.split("~")[0];
        netHoursByMember.set(mid, (netHoursByMember.get(mid) ?? 0) + b.netHours);
      }
      const partyMemberIds = new Set<string>();

      for (const [ev, entries] of eventEntriesByAccount) {
        // Fold the account's raw figures onto the first entry; hide the rest.
        const head = entries[0];
        entries.slice(1).forEach((e) => {
          e.partyHidden = true;
        });
        const netSales = entries.reduce((s, e) => s + e.t.netSales, 0);
        const ccTips = entries.reduce((s, e) => s + e.t.ccTips, 0);
        const autoGrats = entries.reduce((s, e) => s + e.t.autoGrat, 0);
        // The summary row renders the head entry's figures, so fold the account totals onto
        // it (matters only when more than one person clocked into the same Event account).
        head.t.netSales = netSales;
        head.t.ccTips = ccTips;
        head.t.autoGrat = autoGrats;
        head.netHours = entries.reduce((s, e) => s + e.netHours, 0);

        const party = partyState[partyKey(day.dateISO, ev)];
        if (!party?.resolved) continue;
        const ccFeeRate = tipRates.creditCardFee / 100;
        const poolRate = tipRates.tipPool / 100;
        for (const g of party.groups) {
          if (!g.pct) continue;
          const memberIds = [...new Set(g.memberIds)].filter((id) =>
            base.some((b) => !isEventAccount(b.r.name) && b.r.id.split("~")[0] === id),
          );
          if (memberIds.length === 0) continue;
          // Each member's fraction of the whole party (this group's % split evenly).
          const frac = g.pct / 100 / memberIds.length;
          for (const mid of memberIds) {
            partyMemberIds.add(mid);
            const entry = base.find((b) => b.r.id.split("~")[0] === mid);

            // Gross shares, kept split. Party deductions are pool + CC only (no host/bb/bar).
            // Party payments come in on credit card, so the CC fee is charged on BOTH the CC
            // tips and the auto-grats. The deductions are apportioned PROPORTIONALLY across
            // the direct-tips and auto-grat buckets, so the taxable auto is reduced by its
            // fair share (CRA taxes controlled tips, but not on money the staff tipped out).
            const ccTipsShare = ccTips * frac;
            const autoShare = autoGrats * frac;
            const gross = ccTipsShare + autoShare;
            const poolCut = netSales * frac * poolRate;
            const ccFee = gross * ccFeeRate;
            const totalDed = poolCut + ccFee;
            const tipsOwed = gross > 0 ? ccTipsShare - totalDed * (ccTipsShare / gross) : 0;
            const autoOwed = gross > 0 ? autoShare - totalDed * (autoShare / gross) : 0;

            if (entry) {
              entry.partyTipsShare += tipsOwed; // for pay, not shown on their own line
              entry.partyAutoShare += autoOwed;
            }
            head.partyChildren.push({
              memberId: mid,
              name: entry?.r.name ?? mid,
              group: g.name,
              netHours: ownLineMemberIds.has(mid) ? 0 : netHoursByMember.get(mid) ?? 0,
              ccTipsShare,
              autoShare,
              poolCut,
              ccFee,
              tipsOwed,
              autoOwed,
            });
          }
        }
      }

      // Suppress the empty normal row for a party member who has no own tip line — their
      // hours and share live on the party child instead.
      for (const b of base) {
        const mid = b.r.id.split("~")[0];
        if (!isEventAccount(b.r.name) && partyMemberIds.has(mid) && !ownLineMemberIds.has(mid))
          b.partyOnlyHidden = true;
      }

      return base;
    })();

  // One day's tip-out entries, as the Tips tab computes and shows them.
  type DayTips = ReturnType<typeof computeDayTips>;

  // Once Tips is verified, the Tips tab renders the entries frozen at that moment (keyed by
  // day index), not a recomputation — so a later change to the tip-pool engine can't
  // rewrite a verified period. Undefined until verified, or for a period verified before
  // freezing existed (then the tab falls back to the live computation).
  const frozenTips =
    activePeriod && tipsVerified
      ? (frozenStages[activePeriod.id]?.tips as Record<string, DayTips> | undefined)
      : undefined;

  // The Tips tab's computed output for every day, captured to freeze at Verify Tips —
  // byte-for-byte what was on screen, under the period's resolved party pools.
  const buildTipsFreeze = (): Record<string, DayTips> => {
    const out: Record<string, DayTips> = {};
    if (!activePeriod) return out;
    for (const day of activePeriod.days) out[String(day.index)] = computeDayTips(day);
    return out;
  };

  const renderTipoutTable = () => (
    <table className="payflow-table">
      <thead>
        <tr>
          <th rowSpan={2} className="payflow-th-left">Employee</th>
          <th rowSpan={2} className="payflow-th-left">Role</th>
          <th rowSpan={2} className="payflow-th-left">Location</th>
          <th rowSpan={2}>Net Hours</th>
          <th rowSpan={2}>Net Sales</th>
          <th rowSpan={2}>CC Tips</th>
          <th rowSpan={2}>Auto Grat’s</th>
          <th colSpan={5}>TipOuts</th>
          <th colSpan={2}>Tips Owed</th>
          <th rowSpan={2}>Total Owed</th>
        </tr>
        <tr>
          <th className="payflow-th-sub">Pool</th>
          <th className="payflow-th-sub">CC</th>
          <th className="payflow-th-sub">Host</th>
          <th className="payflow-th-sub">BB</th>
          <th className="payflow-th-sub">Bar</th>
          <th className="payflow-th-sub">Tips</th>
          <th className="payflow-th-sub">Auto</th>
        </tr>
      </thead>
      <tbody>
        {filteredDays.length === 0 ? (
          <tr>
            <td colSpan={15} className="payflow-empty">
              No employees match your search.
            </td>
          </tr>
        ) : (
          filteredDays.map((day) => {
            const entries = frozenTips?.[String(day.index)] ?? computeDayTips(day);
            // A pool only exists when 2+ people share the same bar + day/night
            // window (or the same host / barback window). Solo shifts aren't a
            // pool, so they get no tint or hover.
            const poolIdOf = (b: (typeof entries)[number]): string | null => {
              if (isBarShift(b.r))
                return b.pool ? `${b.r.location || "Main Bar"}|${b.win}` : null;
              if (isHostRole(b.r.role)) return `host|${b.win}`;
              if (isBarbackRole(b.r.role)) return `barback|${b.win}`;
              return null;
            };
            const poolCount = new Map<string, number>();
            for (const b of entries) {
              const pid = poolIdOf(b);
              if (!pid) continue;
              if (isBarShift(b.r) && !b.t.hasSales) continue;
              poolCount.set(pid, (poolCount.get(pid) || 0) + 1);
            }

            // Keep the day alphabetical, but pull each pool's members into one contiguous
            // block so it's obvious at a glance who was pooled together. A pool sorts to
            // where its earliest-named member would fall; everyone else sorts on their own
            // name. (A row is only "pooled" when 2+ share the same bar/host/barback window.)
            const groupOf = (b: (typeof entries)[number]): string | null => {
              const pid = poolIdOf(b);
              return pid && (poolCount.get(pid) || 0) >= 2 ? pid : null;
            };
            const anchorByPool = new Map<string, string>();
            for (const b of entries) {
              const g = groupOf(b);
              if (!g) continue;
              const cur = anchorByPool.get(g);
              if (cur === undefined || b.r.name.localeCompare(cur) < 0) anchorByPool.set(g, b.r.name);
            }
            const anchorOf = (b: (typeof entries)[number]): string => {
              const g = groupOf(b);
              return g ? anchorByPool.get(g)! : b.r.name;
            };
            const ordered = [...entries].sort(
              (a, b) =>
                anchorOf(a).localeCompare(anchorOf(b)) ||
                (groupOf(a) || "").localeCompare(groupOf(b) || "") ||
                a.r.name.localeCompare(b.r.name),
            );
            return (
            <React.Fragment key={day.index}>
              <tr className="payflow-day-row">
                <td colSpan={15}>
                  <span className="payflow-day-weekday">{day.weekday}</span>
                  <span className="payflow-day-date">{day.dateLabel}</span>
                </td>
              </tr>
              {ordered.map((b) => {
                // A second Event row folded into the first, or a party-only member whose
                // presence is the party child — neither renders as its own row.
                if (b.partyHidden || b.partyOnlyHidden) return null;

                // Event party account: a compact summary row (raw figures only) with an
                // expander that reveals the per-member split.
                if (isEventAccount(b.r.name)) {
                  const pkey = `${day.index}:${eventAccountName(b.r.name)}`;
                  const open = !!expandedParties[pkey];
                  return (
                    <React.Fragment key={`party-${b.r.id}`}>
                      <tr className="payflow-party-row">
                        <td className="payflow-col-name">
                          <button
                            className="payflow-party-toggle"
                            title={open ? "Hide split" : "Show split"}
                            onClick={() =>
                              setExpandedParties((prev) => ({ ...prev, [pkey]: !open }))
                            }
                          >
                            {open ? "▾" : "▸"}
                          </button>
                          {b.r.name}
                        </td>
                        <td className="payflow-col-role">{b.r.role}</td>
                        <td className="payflow-col-role">
                          {isBarShift(b.r) ? b.r.location || "Main Bar" : "—"}
                        </td>
                        <td>{fmtHours(b.netHours)}</td>
                        <td>{fmtMoney(b.t.netSales)}</td>
                        <td>{fmtMoney(b.t.ccTips)}</td>
                        <td>{fmtMoney(b.t.autoGrat)}</td>
                        {/* Owed columns are intentionally blank on the party line — the
                            split is in the expandable child rows below. */}
                        <td colSpan={8} />
                      </tr>
                      {open &&
                        b.partyChildren.map((c, i) => {
                          const m0 = (v: number) => (v > 0 ? fmtMoney(v) : "—");
                          return (
                            <tr key={`pc-${b.r.id}-${i}`} className="payflow-party-child">
                              <td className="payflow-col-name payflow-party-child-name">
                                {c.name}
                              </td>
                              <td className="payflow-col-role">{c.group}</td>
                              <td className="payflow-col-role">—</td>
                              <td>{c.netHours > 0 ? fmtHours(c.netHours) : "—"}</td>
                              <td>—</td>
                              <td>{m0(c.ccTipsShare)}</td>
                              <td>{m0(c.autoShare)}</td>
                              <td>{m0(c.poolCut)}</td>
                              <td>{m0(c.ccFee)}</td>
                              <td>—</td>
                              <td>—</td>
                              <td>—</td>
                              <td>{m0(c.tipsOwed)}</td>
                              <td>{m0(c.autoOwed)}</td>
                              <td className="payflow-col-net">{fmtMoney(c.tipsOwed + c.autoOwed)}</td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                }

                const tipped = b.t.hasSales;
                const recipient = isHostRole(b.r.role) || isBarbackRole(b.r.role);
                const owed = tipped || recipient;
                const totalOwed = b.tips + b.auto;
                // Only tint/label a row when it's actually part of a pool (2+
                // people on the same shift). Bartenders tint by bar location AND
                // day/night window; host/runner & barback keep their role tint.
                const pid = poolIdOf(b);
                const pooled = pid ? (poolCount.get(pid) || 0) >= 2 : false;
                const meta = !pooled
                  ? null
                  : isBarShift(b.r)
                    ? (() => {
                        const loc = b.r.location || "Main Bar";
                        const lt = LOCATION_TINT[loc];
                        if (!lt) return null;
                        const isNight = b.win === "night";
                        return {
                          label: `${loc} Pool · ${isNight ? "Night" : "Day"}`,
                          color: lt.color,
                          tint: isNight ? lt.tintNight : lt.tintDay,
                        };
                      })()
                    : b.pool
                      ? POOL_META[b.pool]
                      : null;
                const sales = (v: number) => (tipped ? fmtMoney(v) : "—");
                const ded = (v: number, show: boolean) => (show && v > 0 ? fmtMoney(v) : "—");
                return (
                  <tr
                    key={`${b.r.id}-${b.r.seq ?? 0}`}
                    style={meta ? { background: meta.tint } : undefined}
                    title={meta ? `Pool: ${meta.label}` : undefined}
                  >
                    <td
                      className="payflow-col-name"
                      style={meta ? { borderLeft: `3px solid ${meta.color}` } : undefined}
                    >
                      {b.r.name}
                    </td>
                    <td className="payflow-col-role">{b.r.role}</td>
                    <td className="payflow-col-role">
                      {isBarShift(b.r) ? b.r.location || "Main Bar" : "—"}
                    </td>
                    <td>{fmtHours(b.netHours)}</td>
                    <td>{sales(b.t.netSales)}</td>
                    <td>{sales(b.t.ccTips)}</td>
                    <td>{sales(b.t.autoGrat)}</td>
                    <td>{ded(b.tipPool, tipped)}</td>
                    <td>{ded(b.ccFee, tipped)}</td>
                    <td>{ded(b.host, tipped)}</td>
                    <td>{ded(b.barBack, tipped)}</td>
                    <td>{ded(b.bar, tipped)}</td>
                    <td>{owed ? fmtMoney(b.tips) : "—"}</td>
                    {/* Own auto-grats only — a party cut is shown under the Event row, not
                        lumped onto the member's own line. */}
                    <td>{tipped ? fmtMoney(b.auto) : "—"}</td>
                    <td className="payflow-col-net">{owed ? fmtMoney(totalOwed) : "—"}</td>
                  </tr>
                );
              })}
            </React.Fragment>
            );
          })
        )}
      </tbody>
    </table>
  );

  // Did this employee/role work the actual stat holiday in the open period?
  const workedHolidayFor = (empId: string, role: string): boolean => {
    if (!statHolidayISO || !activePeriod) return false;
    return activePeriod.days.some(
      (d) => d.dateISO === statHolidayISO && d.rows.some((r) => r.id === empId && r.role === role),
    );
  };

  // Stat pay = the 4 weeks' regular wages prior to the holiday ÷ 20 (Ontario
  // rule shown in Payroll Rules), applied exactly as it's done by hand: no
  // prior-window hours → 0. A new hire (or anyone with nothing in the four weeks
  // before the holiday) has no wages to divide, so they earn no stat pay.
  const statPayFor = (empId: string, role: string, rate: number): number => {
    if (!statHolidayISO) return 0;
    const holiday = new Date(`${statHolidayISO}T00:00:00`);
    const windowStart = new Date(holiday);
    windowStart.setDate(windowStart.getDate() - 28);
    let priorPay = 0;
    for (const p of payPeriods) {
      for (const day of p.days) {
        const dd = new Date(`${day.dateISO}T00:00:00`);
        if (dd < windowStart || dd >= holiday) continue;
        for (const r of day.rows) {
          if (r.id !== empId || r.role !== role) continue;
          const key = rowKey(p.id, day.index, r.id, r.seq);
          // Each prior period's hours are recomputed under ITS OWN break rules —
          // the ones that produced the pay actually earned in that fortnight — not
          // the open period's. `p.rules` is absent only on payrolls built before
          // rules were snapshotted, which fall back as everywhere else.
          const nh = computeRow(
            r,
            adjustments[key],
            reviews[key],
            p.rules?.breakRules ?? breakRules,
          ).netHours;
          if (Number.isFinite(nh)) priorPay += nh * rate;
        }
      }
    }
    // No fallback: 0/20 = 0 is the correct answer, not a substitute average.
    return priorPay / 20;
  };

  /**
   * The money for one Summary line.
   *
   * Lifted verbatim out of renderSummaryTable's row loop — every expression below is
   * the one that was previously computed inline in the JSX, unchanged. It moved so the
   * same figures can be READ rather than only rendered: the processed-payroll register
   * has to record what was actually paid, and recomputing it beside a second copy of
   * these formulas would leave two definitions to drift apart.
   *
   * One definition, two readers. Nothing here decides anything new.
   */
  // Once Overview is verified (a report verified, or the period skipped), its per-line
  // figures render from what was frozen at that moment (keyed by ovKey), not a
  // recomputation — which is what makes verified rates final. Undefined until verified, or
  // for a period verified before freezing existed (then it falls back to live computation).
  const frozenOverview =
    activePeriod && staffVerified
      ? (frozenStages[activePeriod.id]?.overview as Record<string, OverviewLine> | undefined)
      : undefined;

  const summaryLineFor = (s: (typeof summaryRows)[number]): OverviewLine => {
    const ovKey = `${activePeriod!.id}:${s.id}:${s.role}`;
    const frozen = frozenOverview?.[ovKey];
    if (frozen) return frozen;
    const ov = summaryOverrides[ovKey] || {};
    const rate = ov.rate ?? payConstants.baseHourlyRate;
    const tipPoolHr =
      ov.tipPool ?? (isKitchenRole(s.role) ? payConstants.kitchenTipPoolPerHour : 0);
    const foh = roleIsFoh(s.role);
    const fohOn = foh && !ov.fohOff;
    const base = rate * s.netHours;
    const payTipPool = tipPoolHr * s.netHours;
    const payFoh = fohOn ? payConstants.fohPremiumPerHour * s.netHours : 0;
    // Stat pay (4 wks prior ÷ 20). Goes in the standalone Stat Pay column when the
    // holiday was NOT worked; if it WAS worked the staff member gets normal hourly
    // (Base) plus this in Payments → Stat.
    const statAmt = periodHasStat ? statPayFor(s.id, s.role, rate) : 0;
    const workedStat = periodHasStat && workedHolidayFor(s.id, s.role);
    const vacation = base * payConstants.vacationPayRate;
    // Staff account (on-account purchases) from the uploaded report. While unverified
    // the matched amount is editable so the admin can check the parse against the
    // source PDF. Carried on ONE line per person (their first Summary line) so a
    // multi-role staff member's balance is deducted once, not once per role.
    const carriesStaffAcct = staffAcctRoleByPerson.get(s.id) === s.role;
    const staffIdx =
      staffReport && carriesStaffAcct
        ? staffAccountEntryIndexFor(s.name, staffReport.entries)
        : -1;
    const staffAcct =
      staffReport && staffIdx >= 0 ? staffReport.entries[staffIdx].amount : undefined;
    return {
      ovKey,
      rate,
      tipPoolHr,
      foh,
      fohOn,
      base,
      payTipPool,
      payFoh,
      statAmt,
      workedStat,
      vacation,
      staffAcct,
      // Whether this is the person's staff-account line — the others show nothing
      // in that column, so the balance is entered and read in exactly one place.
      carriesStaffAcct,
    };
  };

  // The Overview tab's computed line for every employee+role, captured to freeze at
  // Overview verification — keyed by ovKey, exactly as summaryLineFor reads it back.
  const buildOverviewFreeze = (): Record<string, OverviewLine> => {
    const out: Record<string, OverviewLine> = {};
    if (!activePeriod) return out;
    for (const s of summaryRows) out[`${activePeriod.id}:${s.id}:${s.role}`] = summaryLineFor(s);
    return out;
  };

  /**
   * Tips owed and auto-grats for the whole period, keyed employee+role.
   *
   * Doubles (id~day / id~night) fold back to the base employee id. Both the Overview
   * and Summary tables built this identically before; it is one definition now, and
   * the processed register reads the same one.
   */
  const periodTipTotals = (): Map<string, { tips: number; auto: number }> => {
    const totals = new Map<string, { tips: number; auto: number }>();
    if (!activePeriod) return totals;
    for (const day of activePeriod.days) {
      for (const b of frozenTips?.[String(day.index)] ?? computeDayTips(day)) {
        // The Event account itself is not a payable employee — it's only a display row.
        if (isEventAccount(b.r.name)) continue;
        const k = `${b.r.id.split("~")[0]}__${b.r.role}`;
        const cur = totals.get(k) || { tips: 0, auto: 0 };
        // Own tips/auto PLUS this member's party cut, kept split (direct tips vs taxed
        // auto-grats). The party cut is paid but never shown on the member's own tip line.
        cur.tips += (b.tips || 0) + (b.partyTipsShare || 0);
        cur.auto += (b.auto || 0) + (b.partyAutoShare || 0);
        totals.set(k, cur);
      }
    }
    return totals;
  };

  /**
   * The register: every figure the Summary tab shows, as data.
   *
   * Built from `summaryLineFor` and `periodTipTotals` — the same two functions the
   * tables render from — so what is recorded as paid is by construction what was on
   * screen when the admin pressed Process. The server stores this verbatim and never
   * recomputes it, which is what stops a later change to the payroll engine rewriting
   * what a finished payroll says.
   */
  const buildRegister = () => {
    const tipTotals = periodTipTotals();
    const lines = summaryRows.map((s) => {
      const line = summaryLineFor(s);
      const tt = tipTotals.get(`${s.id}__${s.role}`) || { tips: 0, auto: 0 };
      return {
        empId: s.id,
        name: s.name,
        role: s.role,
        netHours: s.netHours,
        rate: line.rate,
        base: line.base,
        // Stored as the amounts actually payable, matching what the table prints: a
        // rate of zero prints nothing and contributes nothing.
        tipPool: line.tipPoolHr > 0 ? line.payTipPool : 0,
        foh: line.fohOn ? line.payFoh : 0,
        statPay: line.statAmt,
        statWorked: line.workedStat,
        tips: tt.tips,
        controlledTips: tt.auto,
        staffAccount: line.staffAcct ?? 0,
        vacation: line.vacation,
      };
    });
    const sum = (pick: (l: (typeof lines)[number]) => number) =>
      lines.reduce((total, l) => total + pick(l), 0);
    return {
      lines,
      totals: {
        netHours: sum((l) => l.netHours),
        base: sum((l) => l.base),
        tipPool: sum((l) => l.tipPool),
        foh: sum((l) => l.foh),
        statPay: sum((l) => l.statPay),
        tips: sum((l) => l.tips),
        controlledTips: sum((l) => l.controlledTips),
        staffAccount: sum((l) => l.staffAccount),
        vacation: sum((l) => l.vacation),
      },
    };
  };

  // Payroll tab. Final per-employee payroll register, one line per employee+role
  // (same source rows as the Summary), searchable. Values are pulled from the
  // Summary and Tips tabs so the two stay consistent. Columns still awaiting a
  // real source (Salary, 2026 Stat Pay, Vacation Pay, Stat Holiday Pay, Bonus,
  // Accrued Vacation) show a fixed placeholder for now.
  const renderPayrollTable = () => {
    const term = search.trim().toLowerCase();

    // Once processed, the Summary tab shows the stored register — the figures that were
    // actually paid and keyed into QBO — never a recomputation that a later change to the
    // tip/stat/rules engine could silently move. Falls through to the live computation only
    // for a processed payroll old enough to have no stored register (register predates it).
    const frozen = periodProcessed ? registers[activePeriod!.id] ?? undefined : undefined;
    if (frozen) {
      const lines = term
        ? frozen.lines.filter(
            (l) => l.name.toLowerCase().includes(term) || l.role.toLowerCase().includes(term),
          )
        : frozen.lines;
      const vacPct = Math.round(payConstants.vacationPayRate * 100);
      return (
        <table className="payflow-table payflow-summary-table">
          <thead>
            <tr>
              <th className="payflow-th-left">Employee Name</th>
              <th>Salary</th>
              <th>Regular Pay</th>
              <th>2026 Stat Pay</th>
              <th>Vacation Pay</th>
              <th>Stat Holiday Pay</th>
              <th>Bonus</th>
              <th>Staff Account</th>
              <th>Tips</th>
              <th>Controlled Tips</th>
              <th>Stat Pay</th>
              <th>FOH</th>
              <th>Tip Pool</th>
              <th>Accrued Vacation</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={14} className="payflow-empty">
                  {term ? "No employees match your search." : "No hours to summarise."}
                </td>
              </tr>
            ) : (
              lines.map((l) => {
                const sal = isSalaried(l.empId);
                return (
                  <tr key={`${l.empId}:${l.role}`}>
                    <td className="payflow-col-name">{l.name}</td>
                    {sal ? (
                      <td>{fmtMoney(0)}</td>
                    ) : (
                      <td className="payflow-payroll-placeholder">—</td>
                    )}
                    {sal ? (
                      <td className="payflow-payroll-placeholder">—</td>
                    ) : (
                      <td className="payflow-col-net">{fmtHours(l.netHours)}</td>
                    )}
                    <td className="payflow-payroll-placeholder">0h</td>
                    <td className="payflow-payroll-placeholder">{sal ? "0%" : `${vacPct}%`}</td>
                    <td className="payflow-payroll-placeholder">0h</td>
                    <td />
                    <td className={l.staffAccount > 0 ? "payflow-hl-red" : ""}>
                      {l.staffAccount > 0 ? `-${fmtMoney(l.staffAccount)}` : ""}
                    </td>
                    <td>{l.tips > 0 ? fmtMoney(l.tips) : ""}</td>
                    <td>{l.controlledTips > 0 ? fmtMoney(l.controlledTips) : ""}</td>
                    <td>{!l.statWorked && l.statPay > 0 ? fmtMoney(l.statPay) : ""}</td>
                    <td>{l.foh > 0 ? fmtMoney(l.foh) : ""}</td>
                    <td>{l.tipPool > 0 ? fmtMoney(l.tipPool) : ""}</td>
                    <td>{l.statWorked && l.statPay > 0 ? fmtMoney(l.statPay) : ""}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      );
    }

    const rows = term
      ? summaryRows.filter(
          (s) => s.name.toLowerCase().includes(term) || s.role.toLowerCase().includes(term),
        )
      : summaryRows;

    const tipTotals = periodTipTotals();

    return (
      <table className="payflow-table payflow-summary-table">
        <thead>
          <tr>
            <th className="payflow-th-left">Employee Name</th>
            <th>Salary</th>
            <th>Regular Pay</th>
            <th>2026 Stat Pay</th>
            <th>Vacation Pay</th>
            <th>Stat Holiday Pay</th>
            <th>Bonus</th>
            <th>Staff Account</th>
            <th>Tips</th>
            <th>Controlled Tips</th>
            <th>Stat Pay</th>
            <th>FOH</th>
            <th>Tip Pool</th>
            <th>Accrued Vacation</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={14} className="payflow-empty">
                {term ? "No employees match your search." : "No hours to summarise."}
              </td>
            </tr>
          ) : (
            rows.map((s) => {
              // The same line the Summary tab computes. This table used to derive it
              // a second time from the same inputs — two copies of one formula set,
              // which is how the two screens quietly disagree.
              const { ovKey, tipPoolHr, fohOn, payTipPool, payFoh, statAmt, workedStat, staffAcct } =
                summaryLineFor(s);
              const tt = tipTotals.get(`${s.id}__${s.role}`) || { tips: 0, auto: 0 };
              // Salaried staff draw a fixed salary, so Regular Pay is blank and the
              // vacation-accrual rate is 0% for them.
              const sal = isSalaried(s.id);
              return (
                <tr key={ovKey}>
                  <td className="payflow-col-name">{s.name}</td>
                  {/* Salary — a figure for salaried staff (amount TBD, shown as 0.00),
                      "—" (N/A) for hourly staff. */}
                  {sal ? (
                    <td>{fmtMoney(0)}</td>
                  ) : (
                    <td className="payflow-payroll-placeholder">—</td>
                  )}
                  {/* Regular Pay ← Summary Net Hours. Salaried staff show "—" (not a
                      hidden 0) so it's clearly N/A rather than a miscalculation. */}
                  {sal ? (
                    <td className="payflow-payroll-placeholder">—</td>
                  ) : (
                    <td className="payflow-col-net">{fmtHours(s.netHours)}</td>
                  )}
                  {/* 2026 Stat Pay — placeholder */}
                  <td className="payflow-payroll-placeholder">0h</td>
                  {/* Vacation Pay — committed vacation rate (0% for salaried) */}
                  <td className="payflow-payroll-placeholder">
                    {sal ? "0%" : `${Math.round(payConstants.vacationPayRate * 100)}%`}
                  </td>
                  {/* Stat Holiday Pay — placeholder */}
                  <td className="payflow-payroll-placeholder">0h</td>
                  {/* Bonus — blank */}
                  <td />
                  {/* Staff Account ← Summary Staff Account, negative deduction,
                      red-highlighted (Hours-tab style) when there's a charge */}
                  <td className={staffAcct && staffAcct > 0 ? "payflow-hl-red" : ""}>
                    {staffAcct !== undefined && staffAcct > 0 ? `-${fmtMoney(staffAcct)}` : ""}
                  </td>
                  {/* Tips ← Tips Owed → Tips */}
                  <td>{tt.tips > 0 ? fmtMoney(tt.tips) : ""}</td>
                  {/* Controlled Tips ← Tips Owed → Auto */}
                  <td>{tt.auto > 0 ? fmtMoney(tt.auto) : ""}</td>
                  {/* Stat Pay ← Summary standalone Stat Pay (paid when not worked) */}
                  <td>{periodHasStat && !workedStat && statAmt > 0 ? fmtMoney(statAmt) : ""}</td>
                  {/* FOH ← Summary Payments → FOH */}
                  <td>{fohOn ? fmtMoney(payFoh) : ""}</td>
                  {/* Tip Pool ← Summary Payments → Tip Pool */}
                  <td>{tipPoolHr > 0 ? fmtMoney(payTipPool) : ""}</td>
                  {/* Accrued Vacation — a worked-holiday employee's in-lieu day,
                      paid out this period at the same ÷20 figure the not-worked
                      staff get in Stat Pay. Blank otherwise. */}
                  <td>{periodHasStat && workedStat && statAmt > 0 ? fmtMoney(statAmt) : ""}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    );
  };

  // Summary report. Rate & per-hour Tip Pool are editable (Adjust-style chips);
  // FOH is a per-line toggle. The Stat columns appear only when a committed stat
  // holiday falls in this period. Controlled Tips & Staff Account are scaffolded
  // but left blank until their data sources are defined.
  const renderSummaryTable = () => {
    const term = search.trim().toLowerCase();
    const rows = term
      ? summaryRows.filter(
          (s) => s.name.toLowerCase().includes(term) || s.role.toLowerCase().includes(term),
        )
      : summaryRows;
    // Aggregate Auto Grats (Controlled Tips) over the whole period, keyed by
    // employee+role. Doubles (id~day / id~night) fold back to the base id.
    const tipTotals = new Map<string, { tips: number; auto: number }>();
    if (activePeriod) {
      for (const day of activePeriod.days) {
        for (const b of frozenTips?.[String(day.index)] ?? computeDayTips(day)) {
          // Event accounts are display-only; a member's party cut rides in partyTipsShare/
          // partyAutoShare, kept split (direct vs taxed auto), same as periodTipTotals.
          if (isEventAccount(b.r.name)) continue;
          const k = `${b.r.id.split("~")[0]}__${b.r.role}`;
          const cur = tipTotals.get(k) || { tips: 0, auto: 0 };
          cur.tips += (b.tips || 0) + (b.partyTipsShare || 0);
          cur.auto += (b.auto || 0) + (b.partyAutoShare || 0);
          tipTotals.set(k, cur);
        }
      }
    }
    const paymentsCols = (periodHasStat ? 4 : 3) + 1; // Base, Tip Pool, FOH (+Stat) +Vacation
    const statCol = periodHasStat ? 1 : 0; // standalone "Stat Pay" column
    const totalCols = 3 + 3 + statCol + paymentsCols + 2;
    return (
    <table className="payflow-table payflow-summary-table">
      <thead>
        <tr>
          <th rowSpan={2} className="payflow-th-left">Name</th>
          <th rowSpan={2} className="payflow-th-left">Role</th>
          <th rowSpan={2}>Net<br />Hours</th>
          <th colSpan={3}>Per Hour Pay</th>
          {periodHasStat && <th rowSpan={2}>Stat<br />Pay</th>}
          <th colSpan={paymentsCols}>Payments</th>
          <th rowSpan={2}>Controlled<br />Tips</th>
          <th rowSpan={2}>Staff<br />Account</th>
        </tr>
        <tr>
          <th className="payflow-th-sub">Rate</th>
          <th className="payflow-th-sub">Tip Pool</th>
          <th className="payflow-th-sub">FOH</th>
          <th className="payflow-th-sub">Base</th>
          <th className="payflow-th-sub">Tip Pool</th>
          <th className="payflow-th-sub">FOH</th>
          {periodHasStat && <th className="payflow-th-sub">Stat</th>}
          <th className="payflow-th-sub">Vacation</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={totalCols} className="payflow-empty">
              {term ? "No employees match your search." : "No hours to summarise."}
            </td>
          </tr>
        ) : (
          rows.map((s) => {
            const {
              ovKey,
              rate,
              tipPoolHr,
              foh,
              fohOn,
              base,
              payTipPool,
              payFoh,
              statAmt,
              workedStat,
              vacation,
              staffAcct,
              carriesStaffAcct,
            } = summaryLineFor(s);
            // Salaried staff are paid a fixed salary, so the hourly figures (rate,
            // tip pool, vacation) do not apply and their cells are left blank.
            const sal = isSalaried(s.id);
            return (
              <tr key={ovKey}>
                <td className="payflow-col-name">{s.name}</td>
                <td className="payflow-col-role">{s.role}</td>
                <td className="payflow-col-net">{fmtHours(s.netHours)}</td>
                {/* Per Hour Pay → Rate (editable; blank for salaried) */}
                <td className="payflow-adjust-cell">
                  {sal ? (
                    <span className="payflow-payroll-placeholder">—</span>
                  ) : (
                    <button
                      type="button"
                      className="payflow-adjust-chip"
                      disabled={staffVerified || periodProcessed}
                      title={
                        staffVerified || periodProcessed
                          ? "Overview verified — locked"
                          : "Edit hourly rate"
                      }
                      onClick={() =>
                        setSummaryEdit({
                          key: ovKey,
                          field: "rate",
                          label: "Rate",
                          employee: s.name,
                          value: rate.toFixed(2),
                        })
                      }
                    >
                      <span className="payflow-adjust-val">{fmtMoney(rate)}</span>
                      {!staffVerified && !periodProcessed && (
                        <span className="payflow-adjust-edit">✎</span>
                      )}
                    </button>
                  )}
                </td>
                {/* Per Hour Pay → Tip Pool (editable; blank for salaried) */}
                <td className="payflow-adjust-cell">
                  {sal ? (
                    <span className="payflow-payroll-placeholder">—</span>
                  ) : (
                    <button
                      type="button"
                      className="payflow-adjust-chip"
                      disabled={staffVerified || periodProcessed}
                      title={
                        staffVerified || periodProcessed
                          ? "Overview verified — locked"
                          : "Edit tip-pool rate"
                      }
                      onClick={() =>
                        setSummaryEdit({
                          key: ovKey,
                          field: "tipPool",
                          label: "Tip Pool",
                          employee: s.name,
                          value: tipPoolHr.toFixed(2),
                        })
                      }
                    >
                      <span className="payflow-adjust-val">{fmtMoney(tipPoolHr)}</span>
                      {!staffVerified && !periodProcessed && (
                        <span className="payflow-adjust-edit">✎</span>
                      )}
                    </button>
                  )}
                </td>
                {/* Per Hour Pay → FOH (checkbox; on for Host / Food Runner) */}
                <td className="payflow-summary-foh">
                  {foh ? (
                    <input
                      type="checkbox"
                      checked={fohOn}
                      disabled={staffVerified || periodProcessed}
                      title={`FOH premium (+$${payConstants.fohPremiumPerHour}/hr) applies to this shift`}
                      onChange={() =>
                        payflowActions.setSummaryOverride(
                          ovKey,
                          { fohOff: fohOn },
                          currentUser,
                          `${s.name} · FOH`,
                        )
                      }
                    />
                  ) : null}
                </td>
                {/* Standalone Stat Pay — paid when the holiday was NOT worked */}
                {periodHasStat && <td>{!workedStat ? fmtMoney(statAmt) : ""}</td>}
                {/* Payments → Base / Tip Pool / FOH / Stat / Vacation */}
                <td>{fmtMoney(base)}</td>
                <td>{tipPoolHr > 0 ? fmtMoney(payTipPool) : ""}</td>
                <td>{fohOn ? fmtMoney(payFoh) : ""}</td>
                {periodHasStat && <td>{workedStat ? fmtMoney(statAmt) : ""}</td>}
                <td>{sal ? "—" : fmtMoney(vacation)}</td>
                {/* Controlled Tips ← Tips Owed → Auto */}
                {(() => {
                  const auto = tipTotals.get(`${s.id}__${s.role}`)?.auto || 0;
                  return <td>{auto > 0 ? fmtMoney(auto) : ""}</td>;
                })()}
                {/* Staff Account — shown on the person's one carrying line only, so
                    a multi-role staff member's balance is entered and read once.
                    Their other role lines leave this column blank. */}
                <td className="payflow-adjust-cell">
                  {(() => {
                    if (!carriesStaffAcct) return null;
                    const staffVal = staffAcct ?? 0;
                    // Red highlight (Hours-tab style) only when there's an actual
                    // charge; a $0.00 total stays white.
                    const redClass = staffVal > 0 ? " payflow-hl-red" : "";
                    if (!staffVerified && !periodProcessed) {
                      return (
                        <button
                          type="button"
                          className={`payflow-adjust-chip${redClass}`}
                          title="Edit staff-account amount"
                          onClick={() =>
                            setSummaryEdit({
                              key: ovKey,
                              field: "staffAccount",
                              label: "Staff Account",
                              employee: s.name,
                              value: staffVal.toFixed(2),
                            })
                          }
                        >
                          <span className="payflow-adjust-val">{fmtMoney(staffVal)}</span>
                          <span className="payflow-adjust-edit">✎</span>
                        </button>
                      );
                    }
                    return <span className={redClass.trim()}>{fmtMoney(staffVal)}</span>;
                  })()}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
    );
  };

  const openAdjustModal = (key: string, employee: string, day: Day, c: RowComputation) => {
    setAdjustModal({
      key,
      employee,
      dayLabel: `${day.weekday}, ${day.dateLabel}`,
      schedTotal: c.schedTotal,
      value: c.adjustNum.toFixed(2),
      reason: c.isManual ? c.reason : "",
      isExisting: c.isManual,
    });
  };

  const saveAdjustModal = () => {
    if (!adjustModal) return;
    const reason = adjustModal.reason.trim();
    if (!reason) return;
    payflowActions.setAdjustment(
      adjustModal.key,
      parseFloat(adjustModal.value) || 0,
      reason,
      currentUser,
      `${adjustModal.employee} · ${adjustModal.dayLabel}`,
    );
    setAdjustModal(null);
  };

  return (
    <div className={`payflow-page${activePeriod ? " payflow-page-frozen" : ""}`}>
      <h1 className="payflow-title">Manage PayFlow</h1>

      {syncError && (
        <div className="payflow-warning-banner payflow-sync-banner">
          <span>⚠ {syncError}</span>
          {/* A build refused for want of data becomes one click: pull exactly
              that fortnight, then build it once the data lands. */}
          {coverageGap && (
            <button
              className="payflow-sync-banner-action"
              onClick={() => pullForPeriod(coverageGap.endISO)}
            >
              Pull Data for this period →
            </button>
          )}
          <button
            className="payflow-sync-banner-close"
            title="Dismiss"
            onClick={() => payflowActions.clearSyncError()}
          >
            ✕
          </button>
        </div>
      )}

      {/* Tabs: Overview + Payroll Rules + one archive tab per year + add.
          Individual pay periods open via the View button, not a tab. */}
      <div className="payflow-tabs">
        <div
          className={`payflow-tab${view === "overview" ? " payflow-tab-active" : ""}`}
          onClick={() => setView("overview")}
        >
          Overview
        </div>
        <div
          className={`payflow-tab${view === "rules" ? " payflow-tab-active" : ""}`}
          onClick={() => setView("rules")}
        >
          Payroll Rules
        </div>
        <div
          className={`payflow-tab${view === "audit" ? " payflow-tab-active" : ""}`}
          onClick={() => setView("audit")}
        >
          Audit Logs
        </div>
        {years.map((y) => (
          <div
            key={y}
            className={`payflow-tab${activeYear === y ? " payflow-tab-active" : ""}`}
            onClick={() => setView(`year:${y}`)}
          >
            {y}
          </div>
        ))}
      </div>

      {view === "overview" ? (
        renderPeriodList("Pay Periods", recentPeriods, () =>
          // A build already running takes priority — show its progress instead.
          building ? setBuildHidden(false) : setAddPeriodOpen(true),
        )
      ) : activeYear != null ? (
        renderPeriodList(`${activeYear} — Pay Period Archive`, yearPeriods)
      ) : view === "rules" ? (
        <div className="payflow-card">
          <div className="payflow-card-header">
            <div className="payflow-card-title">Payroll Rules</div>
          </div>
          <div className="payflow-control-grid">
            {/* Ontario public holidays */}
            <section className="payflow-control-panel">
              <div className="payflow-control-h-row">
                <h3 className="payflow-control-h">
                  Ontario Public Holidays (Stat){anyCommitted ? ` – ${currentYear}` : ""}
                </h3>
                <button
                  className="payflow-help-toggle"
                  aria-expanded={!!helpOpen.holidays}
                  title="What's this?"
                  onClick={() => toggleHelp("holidays")}
                >
                  ?
                </button>
              </div>
              {helpOpen.holidays && (
                <p className="payflow-control-note">
                  Stat holiday pay is calculated by dividing the 4 weeks’ pay prior to the holiday
                  by 20. This is paid to employees who do not work on the holiday. If the employee
                  works on the holiday, they are paid normal rate for the holiday, and provided with
                  a paid vacation day later in the period. Vacation pay for the day in lieu is
                  calculated using the same 4 weeks’ pay prior to the holiday divided by 20.
                </p>
              )}

              <div className="payflow-holiday-list">
                {holidayRows.map(({ def, fixedISO, date, locked }) => (
                  <div key={def.key} className="payflow-holiday-row">
                    <span className="payflow-holiday-name">{def.name}</span>
                    {locked ? (
                      <span className="payflow-holiday-date">
                        {fmtISODate(date)}
                        {/* Lock icon only while reopened — flags the dates that
                            can't be changed because they've already passed. */}
                        {hasUnlockedRows && <span className="payflow-holiday-lock"> 🔒</span>}
                      </span>
                    ) : fixedISO ? (
                      <span className="payflow-holiday-date">{fmtISODate(fixedISO)}</span>
                    ) : (
                      <input
                        type="date"
                        className="payflow-control-input payflow-control-date payflow-holiday-input"
                        min={`${currentYear}-01-01`}
                        max={`${currentYear}-12-31`}
                        value={holidayDrafts[holidayDraftKey(currentYear, def.key)] || ""}
                        onChange={(e) =>
                          payflowActions.setHolidayDraft(currentYear, def.key, e.target.value)
                        }
                      />
                    )}
                  </div>
                ))}
              </div>

              {archivedYears.length > 0 && (
                <div className="payflow-holiday-archive">
                  <div className="payflow-holiday-archive-h">Archive</div>
                  {archivedYears.map((y) => (
                    <details key={y} className="payflow-holiday-archive-year">
                      <summary>{y}</summary>
                      <div className="payflow-holiday-archive-list">
                        {liveCommittedHolidays[y].map((h) => (
                          <div key={h.key}>
                            {h.name} — {fmtISODate(h.date)}
                          </div>
                        ))}
                      </div>
                    </details>
                  ))}
                </div>
              )}

              <div className="payflow-commit-footer">
                {hasUnlockedRows ? (
                  <button
                    className="payflow-commit-btn payflow-commit-btn-ready"
                    disabled={!allHolidaysAssigned}
                    onClick={() => setHolidayCommitOpen(true)}
                  >
                    Commit
                  </button>
                ) : (
                  <button
                    className="payflow-commit-btn payflow-commit-btn-locked"
                    title={hasFutureLocked ? "Locked — click to reopen" : "Locked"}
                    onClick={() => hasFutureLocked && setUnlockReason("")}
                  >
                    🔒 Locked
                  </button>
                )}
              </div>
            </section>

            {/* Middle column: Unpaid Break Rules stacked over FOH Roles so the
                pair matches the height of the Holidays / Tip Calculations panels. */}
            <div className="payflow-control-col">
            {/* Unpaid break rules */}
            <section className="payflow-control-panel">
              <div className="payflow-control-h-row">
                <h3 className="payflow-control-h">
                  Unpaid Break Rules{liveBreakRulesLock ? " — Locked" : ""}
                </h3>
                <button
                  className="payflow-help-toggle"
                  aria-expanded={!!helpOpen.breaks}
                  title="What's this?"
                  onClick={() => toggleHelp("breaks")}
                >
                  ?
                </button>
                {!liveBreakRulesLock && (
                  <button
                    className="payflow-add-inline"
                    title="Add rule"
                    onClick={() => payflowActions.addBreakRule()}
                  >
                    +
                  </button>
                )}
              </div>
              {helpOpen.breaks && (
                <p className="payflow-control-note">
                  Unpaid breaktime is deducted from total hours based on the Minimum Hours per
                  shift per ESA entitlements.
                </p>
              )}
              <div className="payflow-control-list">
                {liveBreakRules.length === 0 && (
                  <div className="payflow-control-empty">No break rules set.</div>
                )}
                {liveBreakRules.map((rule, i) => (
                  <div key={i} className="payflow-rule-row">
                    <span className="payflow-rule-text">
                      Minimum hours
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        className="payflow-control-num"
                        value={rule.overHours}
                        disabled={!!liveBreakRulesLock}
                        onChange={(e) =>
                          payflowActions.updateBreakRule(i, {
                            overHours: parseFloat(e.target.value) || 0,
                          })
                        }
                      />
                      Unpaid break
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        className="payflow-control-num"
                        value={rule.deductHours}
                        disabled={!!liveBreakRulesLock}
                        onChange={(e) =>
                          payflowActions.updateBreakRule(i, {
                            deductHours: parseFloat(e.target.value) || 0,
                          })
                        }
                      />
                    </span>
                    {!liveBreakRulesLock && (
                      <button
                        className="payflow-rule-remove"
                        title="Remove"
                        onClick={() => payflowActions.removeBreakRule(i)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="payflow-commit-footer">
                {liveBreakRulesLock ? (
                  <button
                    className="payflow-commit-btn payflow-commit-btn-locked"
                    title="Locked — click to unlock"
                    onClick={() => setUnlockBreakReason("")}
                  >
                    🔒 Locked
                  </button>
                ) : (
                  <button
                    className="payflow-commit-btn payflow-commit-btn-ready"
                    onClick={() => setBreakCommitOpen(true)}
                  >
                    Commit
                  </button>
                )}
              </div>
            </section>

            {/* FOH-eligible roles */}
            <section className="payflow-control-panel">
              <div className="payflow-control-h-row">
                <h3 className="payflow-control-h">
                  FOH Roles{liveFohRolesLock ? " — Locked" : ""}
                </h3>
                <button
                  className="payflow-help-toggle"
                  aria-expanded={!!helpOpen.foh}
                  title="What's this?"
                  onClick={() => toggleHelp("foh")}
                >
                  ?
                </button>
              </div>
              {helpOpen.foh && (
                <p className="payflow-control-note">
                  Roles selected here receive the FOH premium which is shown on the Summary.
                </p>
              )}
              <div className="payflow-foh-row">
                <span className="payflow-foh-label">FOH Premium</span>
                {liveFohRolesLock ? (
                  <span className="payflow-rate-value">
                    ${livePayConstants.fohPremiumPerHour}/hr
                  </span>
                ) : (
                  <span className="payflow-rate-input-wrap">
                    <span className="payflow-rate-pct">$</span>
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      className="payflow-control-num"
                      value={livePayConstants.fohPremiumPerHour}
                      onChange={(e) =>
                        payflowActions.setFohPremium(parseFloat(e.target.value) || 0)
                      }
                    />
                    <span className="payflow-rate-pct">/hr</span>
                  </span>
                )}
              </div>
              <div className="payflow-foh-row">
                <span className="payflow-foh-label">FOH Roles</span>
                <div className="payflow-foh-chips">
                  {liveFohRoles.length === 0 ? (
                    <span className="payflow-control-empty">None selected.</span>
                  ) : (
                    liveFohRoles.map((r) => (
                      <span key={r} className="payflow-foh-chip">
                        {r}
                      </span>
                    ))
                  )}
                </div>
                {!liveFohRolesLock && (
                  <button className="payflow-foh-edit" title="Edit FOH roles" onClick={openFohEdit}>
                    ✎ Edit
                  </button>
                )}
              </div>
              <div className="payflow-commit-footer">
                {liveFohRolesLock ? (
                  <button
                    className="payflow-commit-btn payflow-commit-btn-locked"
                    title="Locked — click to unlock"
                    onClick={() => setUnlockFohReason("")}
                  >
                    🔒 Locked
                  </button>
                ) : (
                  <button
                    className="payflow-commit-btn payflow-commit-btn-ready"
                    onClick={() => setFohCommitOpen(true)}
                  >
                    Commit
                  </button>
                )}
              </div>
            </section>
            </div>

            {/* Right column: Tip Calculations stacked over Salaried Employees. */}
            <div className="payflow-control-col">
            {/* Tip calculations */}
            <section className="payflow-control-panel">
              <div className="payflow-control-h-row">
                <h3 className="payflow-control-h">
                  Tip Calculations{liveTipRatesLock ? " — Locked" : ""}
                </h3>
              </div>

              <div className="payflow-rate-list">
                {TIP_RATE_DEFS.map(({ key, label, hint }) => {
                  const helpKey = `tip:${key}`;
                  return (
                    <div key={key} className="payflow-rate-row">
                      <div className="payflow-rate-head">
                        <span className="payflow-rate-label">{label}</span>
                        <button
                          className="payflow-help-toggle"
                          aria-expanded={!!helpOpen[helpKey]}
                          title="What's this?"
                          onClick={() => toggleHelp(helpKey)}
                        >
                          ?
                        </button>
                        {liveTipRatesLock ? (
                          <span className="payflow-rate-value">{liveTipRates[key]}%</span>
                        ) : (
                          <span className="payflow-rate-input-wrap">
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              className="payflow-control-num"
                              value={liveTipRates[key]}
                              onChange={(e) =>
                                payflowActions.setTipRate(key, parseFloat(e.target.value) || 0)
                              }
                            />
                            <span className="payflow-rate-pct">%</span>
                          </span>
                        )}
                      </div>
                      {helpOpen[helpKey] && <div className="payflow-rate-hint">{hint}</div>}
                    </div>
                  );
                })}
              </div>

              <div className="payflow-commit-footer">
                {liveTipRatesLock ? (
                  <button
                    className="payflow-commit-btn payflow-commit-btn-locked"
                    title="Locked — click to unlock"
                    onClick={() => setUnlockTipReason("")}
                  >
                    🔒 Locked
                  </button>
                ) : (
                  <button
                    className="payflow-commit-btn payflow-commit-btn-ready"
                    onClick={() => setTipCommitOpen(true)}
                  >
                    Commit
                  </button>
                )}
              </div>
            </section>

            {/* Salaried employees */}
            <section className="payflow-control-panel">
              <div className="payflow-control-h-row">
                <h3 className="payflow-control-h">
                  Salaried Employees{liveSalariedLock ? " — Locked" : ""}
                </h3>
                <button
                  className="payflow-help-toggle"
                  aria-expanded={!!helpOpen.salaried}
                  title="What's this?"
                  onClick={() => toggleHelp("salaried")}
                >
                  ?
                </button>
              </div>
              {helpOpen.salaried && (
                <p className="payflow-control-note">
                  Employees ticked here are paid a fixed salary, so the hourly figures (rate, tip
                  pool, vacation and regular pay) are left blank for them on the Summary.
                </p>
              )}
              <div className="payflow-foh-row">
                <span className="payflow-foh-label">Salaried</span>
                <div className="payflow-foh-chips">
                  {liveSalariedEmployeeIds.length === 0 ? (
                    <span className="payflow-control-empty">None selected.</span>
                  ) : (
                    liveSalariedEmployeeIds.map((id) => (
                      <span key={id} className="payflow-foh-chip">
                        {availableEmployees.find((e) => e.id === id)?.name ?? id}
                      </span>
                    ))
                  )}
                </div>
                {!liveSalariedLock && (
                  <button
                    className="payflow-foh-edit"
                    title="Edit salaried employees"
                    onClick={openSalariedEdit}
                  >
                    ✎ Edit
                  </button>
                )}
              </div>
              <div className="payflow-commit-footer">
                {liveSalariedLock ? (
                  <button
                    className="payflow-commit-btn payflow-commit-btn-locked"
                    title="Locked — click to unlock"
                    onClick={() => setUnlockSalariedReason("")}
                  >
                    🔒 Locked
                  </button>
                ) : (
                  <button
                    className="payflow-commit-btn payflow-commit-btn-ready"
                    onClick={() => setSalariedCommitOpen(true)}
                  >
                    Commit
                  </button>
                )}
              </div>
            </section>
            </div>
          </div>
        </div>
      ) : view === "audit" ? (
        <div className="payflow-card">
          <div className="payflow-card-header">
            <div className="payflow-card-title">Audit Logs</div>
          </div>
          <div className="payflow-audit-table">
            <div className="payflow-audit-header">
              <div>When</div>
              <div>Action By</div>
              <div>Action</div>
              <div>Reason</div>
              <div>IP</div>
              <div>User Agent</div>
            </div>
            {auditLog.length === 0 ? (
              <div className="payflow-control-empty">No audit entries yet.</div>
            ) : (
              auditLog.map((entry, i) => (
                <div key={i} className="payflow-audit-row">
                  <div className="payflow-audit-when">{new Date(entry.at).toLocaleString()}</div>
                  <div>{entry.by}</div>
                  <div className="payflow-audit-action">{entry.action}</div>
                  <div className="payflow-audit-reason">{entry.reason || "—"}</div>
                  <div className="payflow-audit-ip">{entry.ip || "—"}</div>
                  <div className="payflow-audit-ua" title={entry.userAgent}>
                    {entry.userAgent || "—"}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        activePeriod && (
          <div className="payflow-card">
            <div className="payflow-card-header">
              <div className="payflow-detail-head">
                <button
                  className="payflow-back-btn"
                  title="Back to Overview"
                  onClick={() => setView("overview")}
                >
                  ‹
                </button>
                <div className="payflow-card-title">
                  Pay period ending {periodEnd(activePeriod)}
                </div>
                <div className="payflow-step-toggle">
                  <button
                    className={periodTab === "hours" ? "payflow-step-active" : ""}
                    onClick={() => setPeriodTab("hours")}
                  >
                    Hours
                  </button>
                  <button
                    className={periodTab === "tips" ? "payflow-step-active" : ""}
                    onClick={() => setPeriodTab("tips")}
                  >
                    Tips
                  </button>
                  <button
                    className={periodTab === "summary" ? "payflow-step-active" : ""}
                    onClick={() => setPeriodTab("summary")}
                  >
                    Overview
                  </button>
                  <button
                    className={periodTab === "payroll" ? "payflow-step-active" : ""}
                    onClick={() => setPeriodTab("payroll")}
                  >
                    Summary
                  </button>
                </div>
              </div>

              <div className="payflow-header-right">
                <div className="payflow-search">
                  <span className="payflow-search-icon" aria-hidden="true">⌕</span>
                  <input
                    className="payflow-search-input"
                    type="text"
                    placeholder="Search employees…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                {/* Per-tab Verify / locked button */}
                {periodTab === "hours" &&
                  (periodApproved ? (
                    <button
                      className="payflow-verify-btn payflow-verify-locked"
                      title="Click to unlock"
                      onClick={() => tryUnlockStage(0, () => setUnlockHoursReason(""))}
                    >
                      🔒 Hours Verified
                    </button>
                  ) : (
                    <button
                      className={`payflow-verify-btn payflow-verify-ready${
                        !rulesComplete || hoursOutstanding ? " payflow-verify-blocked" : ""
                      }`}
                      title="Verify and lock hours for this period"
                      onClick={tryVerifyHours}
                    >
                      Verify
                    </button>
                  ))}

                {periodTab === "tips" &&
                  (tipsVerified ? (
                    <button
                      className="payflow-verify-btn payflow-verify-locked"
                      title="Click to unlock"
                      onClick={() => tryUnlockStage(1, () => setUnlockTipsVerifyReason(""))}
                    >
                      🔒 Tips Verified
                    </button>
                  ) : (
                    <button
                      className={`payflow-verify-btn payflow-verify-ready${
                        !rulesComplete || periodOutstanding ? " payflow-verify-blocked" : ""
                      }`}
                      title="Verify and lock tips for this period"
                      onClick={tryVerifyTips}
                    >
                      Verify
                    </button>
                  ))}

                {/* Overview tab → Staff Accounts: upload → verify → locked */}
                {periodTab === "summary" &&
                  (!staffReport ? (
                    <button
                      className="payflow-verify-btn payflow-verify-blue"
                      title="Upload the Staff Accounts report, or mark no activity for this period"
                      onClick={() => setStaffChoiceOpen(true)}
                    >
                      ⤴ Staff Accounts
                    </button>
                  ) : staffVerified ? (
                    <button
                      className="payflow-verify-btn payflow-verify-locked"
                      title={`${
                        staffReport.skipped ? "No staff account activity" : "Verified"
                      }${staffReport.verifiedBy ? ` by ${staffReport.verifiedBy}` : ""} — click to unlock`}
                      onClick={() => tryUnlockStage(2, unlockStaffAccounts)}
                    >
                      🔒 {staffReport.skipped ? "No Staff Activity" : "Overview Verified"}
                    </button>
                  ) : (
                    <button
                      className="payflow-verify-btn payflow-verify-ready"
                      title="Verify the ingested staff-account amounts and continue to Summary"
                      onClick={verifyStaffAccounts}
                    >
                      Verify
                    </button>
                  ))}

                {/* Summary tab → Process */}
                {periodTab === "payroll" &&
                  (periodProcessed ? (
                    <button
                      className="payflow-verify-btn payflow-verify-locked"
                      title="Click to unlock"
                      onClick={() => setUnlockProcessReason("")}
                    >
                      🔒 Processed
                    </button>
                  ) : (
                    <button
                      className={`payflow-verify-btn payflow-verify-ready${
                        !tipsVerified ? " payflow-verify-blocked" : ""
                      }`}
                      title="Process payroll for this period"
                      onClick={tryProcess}
                    >
                      Process
                    </button>
                  ))}

                {/* Hidden file input for Staff Accounts upload (shared) */}
                <input
                  ref={staffFileRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  style={{ display: "none" }}
                  onChange={handleStaffUpload}
                />
              </div>
            </div>

            {/* This payroll predates rule snapshotting, so it has no rules of its own
                and is being computed from the live ones — meaning a rules edit can
                still move its figures. Said plainly rather than papered over with
                defaults, which would look like knowledge we do not have. */}
            {rulesUnsnapshotted && (
              <div className="payflow-warning-banner payflow-warning-banner-persistent">
                ⚠ This payroll was built before payroll rules were recorded against each
                pay period, so its figures are calculated from the rules as they stand
                today and will change if the rules change. Rebuild the pay period to fix
                its rules in place.
              </div>
            )}

            {/* Defects the build found in the source data it assembled. Shown next to
                the numbers they affect rather than left in a server log. */}
            {activePeriod.warnings.map((warning) => (
              <div
                key={warning}
                className="payflow-warning-banner payflow-warning-banner-persistent"
              >
                ⚠ {warning}
              </div>
            ))}


            {/* Both halves, not just the rows. Drawing the tables on the rows alone
                would show hours with no adjustments applied and every stage looking
                unverified, then rewrite itself when the decisions land — and there is
                no way for the person reading it to tell which figure was real. */}
            {!activePeriod.daysLoaded || !activePeriod.decisionsLoaded ? (
              <div className="payflow-loading">
                <span className="payflow-spinner" />
                <span>Loading schedule &amp; POS data…</span>
              </div>
            ) : periodTab === "payroll" ? (
              <div className="payflow-table-wrap">{renderPayrollTable()}</div>
            ) : periodTab === "summary" ? (
              <div className="payflow-table-wrap">
                {periodProcessed && !staffReport && (
                  <div className="payflow-warning-banner payflow-warning-banner-persistent">
                    ⚠ Payroll has been processed without a Staff Account report. Employee account
                    deductions have not been applied.
                  </div>
                )}

                {renderSummaryTable()}
              </div>
            ) : showTipouts ? (
              <div className="payflow-table-wrap">{renderTipoutTable()}</div>
            ) : (
              <div className="payflow-table-wrap">
                <table className="payflow-table">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="payflow-th-left">Employee</th>
                      <th rowSpan={2} className="payflow-th-left">Role</th>
                      <th colSpan={2}>Scheduled</th>
                      <th colSpan={2}>Actual</th>
                      <th colSpan={2}>Total Hours</th>
                      <th rowSpan={2}>Variance</th>
                      <th rowSpan={2}>Adjust</th>
                      <th rowSpan={2}>Break</th>
                      <th rowSpan={2}>Net Hours</th>
                    </tr>
                    <tr>
                      <th>Start</th>
                      <th>End</th>
                      <th>ClockIn</th>
                      <th>ClockOut</th>
                      <th className="payflow-th-sub">Scheduled</th>
                      <th className="payflow-th-sub">Actual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDays.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="payflow-empty">
                          No employees match your search.
                        </td>
                      </tr>
                    ) : (
                      filteredDays.map((day) => (
                        <React.Fragment key={day.index}>
                          <tr className="payflow-day-row">
                            <td colSpan={12}>
                              <span className="payflow-day-weekday">{day.weekday}</span>
                              <span className="payflow-day-date">{day.dateLabel}</span>
                              {isStatHoliday(day.dateISO, committedHolidays) && (
                                <span className="payflow-stat-badge" title="Stat holiday">
                                  ★ Stat Holiday
                                </span>
                              )}
                            </td>
                          </tr>
                          {day.rows.filter((r) => !isHiddenAccount(r.name)).map((r) => {
                            const key = rowKey(activePeriod.id, day.index, r.id, r.seq);
                            // Once Hours is verified, render the frozen row (what was signed
                            // off); fall back to a live computation while open, or for a
                            // period verified before freezing existed.
                            const c =
                              frozenHours?.[key] ??
                              computeRow(r, adjustments[key], reviews[key], breakRules);
                            return (
                              <tr key={key}>
                                <td className="payflow-col-name">{r.name}</td>
                                <td className="payflow-col-role">{r.role}</td>
                                {/* An unmatched row shows "—" on the side the sources had
                                    nothing for: no schedule, or no clock record. Never the
                                    other side's times standing in for the missing one. */}
                                <td>{fmtTimeOrNone(r.schedStart)}</td>
                                <td>{fmtTimeOrNone(r.schedEnd)}</td>
                                <td className={inClass(r.actualIn, r.schedStart)}>
                                  {fmtTimeOrNone(r.actualIn)}
                                </td>
                                <td className={outClass(c.effectiveOut, r.schedEnd)}>
                                  {fmtTimeOrNone(c.effectiveOut)}
                                  {c.outCapped && (
                                    <span
                                      className="payflow-capped-flag"
                                      title={`Capped from ${fmtTimeOrNone(r.actualOut)} (2:30 AM closing rule)`}
                                    >
                                      ✶
                                    </span>
                                  )}
                                  {c.earlyOutDeducted && (
                                    <span
                                      className="payflow-capped-flag"
                                      title={`15 min deducted from ${fmtTimeOrNone(r.actualOut)} (early clock-out)`}
                                    >
                                      ↓
                                    </span>
                                  )}
                                </td>
                                <td>{fmtHours(c.schedTotal)}</td>
                                <td className={deltaClass(c.variance)}>{fmtHours(c.actualTotal)}</td>
                                <td className={`payflow-col-variance ${deltaClass(c.variance)}`}>
                                  {fmtSigned(c.variance)}
                                </td>
                                <td className="payflow-adjust-cell">
                                  {c.status === "pending" ? (
                                    <span
                                      className="payflow-adjust-hold"
                                      title={`Awaiting manager approval — ${exceptionSummary(c)}`}
                                    >
                                      ⏸ On hold
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      className={`payflow-adjust-chip${
                                        c.isManual
                                          ? " payflow-adjust-manual"
                                          : c.status === "confirmed"
                                            ? " payflow-adjust-confirmed"
                                            : ""
                                      }`}
                                      disabled={periodApproved}
                                      title={
                                        periodApproved
                                          ? "Hours approved — locked"
                                          : adjustTooltip(c)
                                      }
                                      onClick={() => openAdjustModal(key, r.name, day, c)}
                                    >
                                      <span className="payflow-adjust-val">{c.adjustValue}</span>
                                      {!periodApproved && (
                                        <span className="payflow-adjust-edit">✎</span>
                                      )}
                                    </button>
                                  )}
                                </td>
                                <td className="payflow-col-break">
                                  {c.status === "pending"
                                    ? "—"
                                    : c.breakDeduct > 0
                                      ? `−${fmtHours(c.breakDeduct)}`
                                      : "0.00"}
                                </td>
                                <td className="payflow-col-net">
                                  {c.status === "pending" ? "—" : fmtHours(c.netHours)}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      )}

      {unlockReason !== null && (
        <div className="payflow-modal-overlay" onClick={() => setUnlockReason(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Reopen Stat Holidays</h2>
            <p className="payflow-modal-sub">
              Reopening releases only future {currentYear} holiday dates for re-assignment;
              dates that have already passed stay locked. This action is recorded in the audit
              log.
            </p>
            <label className="payflow-modal-label">
              Reason (required)
              <textarea
                className="payflow-modal-textarea"
                placeholder="Why are these dates being reopened?"
                value={unlockReason}
                autoFocus
                onChange={(e) => setUnlockReason(e.target.value)}
              />
            </label>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setUnlockReason(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                disabled={!unlockReason.trim()}
                onClick={confirmReopen}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockHoursReason !== null && (
        <div className="payflow-modal-overlay" onClick={() => setUnlockHoursReason(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Unlock Hours</h2>
            <p className="payflow-modal-sub">
              This reopens the verified hours for this pay period for editing. The action is
              recorded in the audit log.
            </p>
            <label className="payflow-modal-label">
              Reason (required)
              <textarea
                className="payflow-modal-textarea"
                placeholder="Why are the hours being unlocked?"
                value={unlockHoursReason}
                autoFocus
                onChange={(e) => setUnlockHoursReason(e.target.value)}
              />
            </label>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setUnlockHoursReason(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                disabled={!unlockHoursReason.trim()}
                onClick={confirmUnlockHours}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockTipsVerifyReason !== null && (
        <div className="payflow-modal-overlay" onClick={() => setUnlockTipsVerifyReason(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Unlock Tips</h2>
            <p className="payflow-modal-sub">
              This reopens the verified tips for this pay period for editing. The action is
              recorded in the audit log.
            </p>
            <label className="payflow-modal-label">
              Reason (required)
              <textarea
                className="payflow-modal-textarea"
                placeholder="Why are the tips being unlocked?"
                value={unlockTipsVerifyReason}
                autoFocus
                onChange={(e) => setUnlockTipsVerifyReason(e.target.value)}
              />
            </label>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setUnlockTipsVerifyReason(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                disabled={!unlockTipsVerifyReason.trim()}
                onClick={confirmUnlockTipsVerify}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {verifyWarning && (
        <div className="payflow-modal-overlay" onClick={() => setVerifyWarning(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">⚠ {verifyWarning.title}</h2>
            <p className="payflow-modal-sub">
              Finish the following before this step can move forward:
            </p>
            <ul className="payflow-warning-list">
              {verifyWarning.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                onClick={() => setVerifyWarning(null)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {staffChoiceOpen && (
        <div className="payflow-modal-overlay" onClick={() => setStaffChoiceOpen(false)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Staff Accounts</h2>
            <p className="payflow-modal-sub">
              Upload the Staff Accounts report for this pay period, or — if there was no
              staff account activity — skip it. Skipping records that no report was expected,
              so the payroll can proceed without one.
            </p>
            <div className="payflow-modal-actions">
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setStaffChoiceOpen(false)}
              >
                Cancel
              </button>
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => {
                  setStaffChoiceOpen(false);
                  skipStaffAccounts();
                }}
              >
                No Activity — Skip
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                onClick={() => {
                  setStaffChoiceOpen(false);
                  triggerStaffUpload();
                }}
              >
                Upload Report
              </button>
            </div>
          </div>
        </div>
      )}

      {staffWarnOpen && (
        <div className="payflow-modal-overlay" onClick={() => setStaffWarnOpen(false)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">⚠ Staff Account Report Not Uploaded</h2>
            <p className="payflow-modal-sub">
              A Staff Account report has not been uploaded for this pay period.
              <br />
              <br />
              Without this report, employee staff account purchases cannot be deducted during
              payroll processing.
              <br />
              <br />
              Would you like to upload the report now or continue processing without it?
            </p>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => {
                  setStaffWarnOpen(false);
                  processPayroll();
                }}
              >
                Continue Without Report
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                onClick={() => {
                  setStaffWarnOpen(false);
                  triggerStaffUpload();
                }}
              >
                Upload Report
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockProcessReason !== null && (
        <div className="payflow-modal-overlay" onClick={() => setUnlockProcessReason(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Unlock Payroll</h2>
            <p className="payflow-modal-sub">
              This reverses the processed payroll for this pay period and reopens it for editing.
              The action is recorded in the audit log.
            </p>
            <label className="payflow-modal-label">
              Reason (required)
              <textarea
                className="payflow-modal-textarea"
                placeholder="Why is the payroll being reopened?"
                value={unlockProcessReason}
                autoFocus
                onChange={(e) => setUnlockProcessReason(e.target.value)}
              />
            </label>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setUnlockProcessReason(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                disabled={!unlockProcessReason.trim()}
                onClick={confirmUnlockProcess}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {summaryEdit && (
        <div className="payflow-modal-overlay" onClick={() => setSummaryEdit(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">{summaryEdit.label}</h2>
            <p className="payflow-modal-sub">
              {summaryEdit.employee} — {summaryEdit.label}{" "}
              {summaryEdit.field === "staffAccount" ? "($)" : "($/hr)"}
            </p>
            <label className="payflow-modal-label">
              {summaryEdit.label} {summaryEdit.field === "staffAccount" ? "($)" : "($/hr)"}
              <input
                type="number"
                step="0.01"
                min="0"
                className="payflow-modal-input"
                value={summaryEdit.value}
                autoFocus
                onChange={(e) =>
                  setSummaryEdit((prev) => (prev ? { ...prev, value: e.target.value } : prev))
                }
              />
            </label>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setSummaryEdit(null)}
              >
                Cancel
              </button>
              <button className="payflow-modal-btn payflow-modal-btn-save" onClick={saveSummaryEdit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {addPeriodOpen && (
        <div className="payflow-modal-overlay" onClick={() => setAddPeriodOpen(false)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Add Pay Period</h2>
            <p className="payflow-modal-sub">
              Select the pay-period ending date (every second Monday).
            </p>
            <div className="payflow-period-picker">
              {addPeriodOptions.length === 0 ? (
                <div className="payflow-control-empty">No pay-period dates available.</div>
              ) : (
                addPeriodOptions.map((o) => (
                  <button
                    key={o.iso}
                    className="payflow-period-option"
                    onClick={() => startBuild(o)}
                  >
                    {o.label}
                    {o.exists && (
                      <span className="payflow-period-option-note"> — exists, will replace</span>
                    )}
                  </button>
                ))
              )}
            </div>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setAddPeriodOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {replaceTarget && (
        <div className="payflow-modal-overlay" onClick={() => setReplaceTarget(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Replace Payroll</h2>
            <p className="payflow-modal-sub">
              This will replace the existing payroll for this period and re-run the data pipeline.
              Decisions made on the current payroll will be discarded.
            </p>
            <p className="payflow-modal-sub">
              <strong>Pay period ending {replaceTarget.label}</strong>
            </p>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setReplaceTarget(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-danger"
                onClick={confirmReplacePeriod}
              >
                Replace
              </button>
            </div>
          </div>
        </div>
      )}

      {building && !buildHidden && (
        <div className="payflow-modal-overlay">
          <div className="payflow-modal">
            <h2 className="payflow-modal-title">Creating Payroll</h2>
            <p className="payflow-modal-sub">
              Pay period ending {fmtISODate(building)}. This uses the data already in
              PayFlow, so it only takes a moment.
            </p>
            <div className="payflow-build-progress">
              <span className="payflow-spinner" />
              <div className="payflow-build-note">Assembling the pay period</div>
            </div>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setBuildHidden(true)}
              >
                Hide
              </button>
            </div>
          </div>
        </div>
      )}

      {pullOpen && (
        <div className="payflow-modal-overlay">
          <div className="payflow-modal">
            <h2 className="payflow-modal-title">
              {pullInFlight ? "Refresh Source Data" : "Refresh Data from 7Shifts & Silverware"}
            </h2>
            {pullInFlight ? (
              <p className="payflow-modal-sub">
                {dataPull?.startedBy === "schedule"
                  ? "The nightly update is running."
                  : dataPull?.windowStart
                    ? `Refreshing data for the period from ${fmtISODate(
                        dataPull.windowStart,
                      )} to ${fmtISODate(dataPull.windowEnd || dataPull.windowStart)}.`
                    : "Refreshing source data."}
              </p>
            ) : (
              <p className="payflow-modal-sub">
                Select up to 15 days at once to refresh. To update Payrolls with this data you
                will need to recreate the Payroll Period.
              </p>
            )}

            {!pullInFlight && (
              <PullRangeCalendar
                startISO={pullStart}
                endISO={pullEnd}
                onChange={(start, end) => {
                  setPullStart(start);
                  setPullEnd(end);
                }}
                maxISO={latestPullDate}
                maxSpanDays={MAX_PULL_SPAN_DAYS}
                coverage={dateCoverage}
                loadedMonths={dateCoverageLoaded}
                onMonthVisible={loadDateCoverage}
              />
            )}

            {/* The calendar will not offer a date past the ceiling or a range over the
                limit, but the range is still checked rather than assumed: the same
                state is seeded from the pay-period config on open, and a stale tab can
                hold a range that was legal when it was seeded and is not now. */}
            {!pullInFlight && (
              <div
                className={`payflow-modal-note${
                  pullRangeError ? " payflow-modal-note-warn" : ""
                }`}
              >
                {pullRangeError ??
                  `Period Selected is ${fmtISODate(pullStart)} to ${fmtISODate(pullEnd)}.`}
              </div>
            )}

            {coverageLine && !pullInFlight && (
              <div className="payflow-modal-note">{coverageLine}</div>
            )}

            {/* The stepper. Shown from the moment the button is pressed rather than
                when the server confirms: the two round trips before `running` arrives
                are the ones that most look like nothing happened. */}
            {pullInFlight && (
              <div className="payflow-build-progress">
                <span className="payflow-spinner" />
                {stages.length > 0 ? (
                  <div className="payflow-timeline payflow-build-steps">
                    {stages.map((stage) => (
                      <div
                        key={stage.key}
                        className={`payflow-tl-step${
                          stage.state === "done" ? " payflow-tl-done" : ""
                        }${stage.state === "active" ? " payflow-tl-active" : ""}${
                          stage.state === "failed" ? " payflow-tl-failed" : ""
                        }`}
                      >
                        <span className="payflow-tl-dot" />
                        <div className="payflow-tl-body">
                          <div className="payflow-tl-name">{stage.label}</div>
                          {stage.state === "active" && (
                            <div className="payflow-tl-meta">{dataPull?.step}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="payflow-build-note">
                    {pullStarting
                      ? "Asking the pipeline to start…"
                      : pullConfirming
                        ? // The run exists — the pipeline said so when it answered. This
                          // is the wait for it to become visible here.
                          "Started. Waiting for the pipeline to report progress…"
                        : (dataPull?.step ?? "Working…")}
                  </div>
                )}
              </div>
            )}

            {!pullInFlight && dataPull && dataPull.state !== "idle" && (
              <div
                className={`payflow-modal-note${
                  dataPull.state === "succeeded" ? "" : " payflow-modal-note-warn"
                }`}
              >
                {dataPull.step}
              </div>
            )}

            {/* Dismissable, and it stays until it is dismissed or another pull is
                asked for. It used to be erased by the next successful status poll,
                which meant the message an admin most needed to read — "that did not
                start" — was the one that vanished fastest. */}
            {dataPullError && (
              <div className="payflow-modal-note payflow-modal-note-warn payflow-modal-note-dismissable">
                <span>{dataPullError.message}</span>
                <button
                  type="button"
                  className="payflow-note-dismiss"
                  onClick={() => payflowActions.dismissDataPullError()}
                  aria-label="Dismiss this message"
                >
                  ×
                </button>
              </div>
            )}

            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setPullOpen(false)}
              >
                Close
              </button>
              {/* Hidden for the whole time a pull of ours is in flight, not just while
                  the server calls it running — otherwise the button reappears during
                  the confirming window and invites a second run against a pipeline
                  that already has one. */}
              {!pullInFlight && (
                <button
                  className="payflow-modal-btn payflow-modal-btn-save"
                  disabled={!!pullRangeError}
                  onClick={() =>
                    void payflowActions.startDataPull({
                      startDate: pullStart,
                      endDate: pullEnd,
                    })
                  }
                >
                  Refresh Data
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!deletePeriodTarget}
        onClose={() => setDeletePeriodTarget(null)}
        onConfirm={confirmDeletePeriod}
        title="Delete Pay Period"
        message={
          deletePeriodTarget
            ? `Are you sure you want to delete the pay period ending ${periodEnd(
                deletePeriodTarget,
              )}? All of its data will be lost. This cannot be undone.`
            : ""
        }
      />

      {historyPeriod && (
        <div className="payflow-modal-overlay" onClick={() => setHistoryPeriod(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Status History</h2>
            <p className="payflow-modal-sub">Payroll ending {periodEnd(historyPeriod)}</p>
            <div className="payflow-timeline">
              {periodTimeline(historyPeriod).map((s, i) => (
                <div key={i} className={`payflow-tl-step${s.done ? " payflow-tl-done" : ""}`}>
                  <span className="payflow-tl-dot" />
                  <div className="payflow-tl-body">
                    <div className="payflow-tl-name">{s.name}</div>
                    <div className="payflow-tl-meta">
                      {s.done ? `${s.by ? `${s.by} · ` : ""}${s.at}` : "Pending"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setHistoryPeriod(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {breakCommitOpen && (
        <div className="payflow-modal-overlay" onClick={() => setBreakCommitOpen(false)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Commit Break Rules</h2>
            <p className="payflow-modal-sub">
              This locks the unpaid-break rules for payroll. Unlocking later requires a reason and
              is recorded in the audit log.
            </p>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setBreakCommitOpen(false)}
              >
                Cancel
              </button>
              <button className="payflow-modal-btn payflow-modal-btn-save" onClick={commitBreakRules}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockBreakReason !== null && (
        <div className="payflow-modal-overlay" onClick={() => setUnlockBreakReason(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Unlock Break Rules</h2>
            <p className="payflow-modal-sub">
              This reopens the unpaid-break rules for editing. The action is recorded in the audit
              log.
            </p>
            <label className="payflow-modal-label">
              Reason (required)
              <textarea
                className="payflow-modal-textarea"
                placeholder="Why are the break rules being unlocked?"
                value={unlockBreakReason}
                autoFocus
                onChange={(e) => setUnlockBreakReason(e.target.value)}
              />
            </label>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setUnlockBreakReason(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                disabled={!unlockBreakReason.trim()}
                onClick={confirmUnlockBreak}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {tipCommitOpen && (
        <div className="payflow-modal-overlay" onClick={() => setTipCommitOpen(false)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Commit Tip Rates</h2>
            <p className="payflow-modal-sub">
              This locks the tip-calculation percentages for payroll. Unlocking later requires a
              reason and is recorded in the audit log.
            </p>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setTipCommitOpen(false)}
              >
                Cancel
              </button>
              <button className="payflow-modal-btn payflow-modal-btn-save" onClick={commitTipRates}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockTipReason !== null && (
        <div className="payflow-modal-overlay" onClick={() => setUnlockTipReason(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Unlock Tip Rates</h2>
            <p className="payflow-modal-sub">
              This reopens the tip-calculation percentages for editing. The action is recorded in
              the audit log.
            </p>
            <label className="payflow-modal-label">
              Reason (required)
              <textarea
                className="payflow-modal-textarea"
                placeholder="Why are the tip rates being unlocked?"
                value={unlockTipReason}
                autoFocus
                onChange={(e) => setUnlockTipReason(e.target.value)}
              />
            </label>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setUnlockTipReason(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                disabled={!unlockTipReason.trim()}
                onClick={confirmUnlockTips}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {fohEditOpen && (
        <div className="payflow-modal-overlay" onClick={() => setFohEditOpen(false)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">FOH Roles</h2>
            <p className="payflow-modal-sub">
              Select the roles eligible for the FOH premium (+${livePayConstants.fohPremiumPerHour}
              /hr).
            </p>
            <div className="payflow-foh-picker">
              {availableRoles.length === 0 ? (
                <div className="payflow-control-empty">No roles found in the schedule.</div>
              ) : (
                availableRoles.map((role) => (
                  <label key={role} className="payflow-foh-check">
                    <input
                      type="checkbox"
                      checked={fohDraft.includes(role)}
                      onChange={() => toggleFohDraftRole(role)}
                    />
                    <span>{role}</span>
                  </label>
                ))
              )}
            </div>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setFohEditOpen(false)}
              >
                Cancel
              </button>
              <button className="payflow-modal-btn payflow-modal-btn-save" onClick={saveFohEdit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {fohCommitOpen && (
        <div className="payflow-modal-overlay" onClick={() => setFohCommitOpen(false)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Commit FOH Roles</h2>
            <p className="payflow-modal-sub">
              This locks the FOH premium of{" "}
              <strong>${livePayConstants.fohPremiumPerHour}/hr</strong> and which roles receive it:{" "}
              <strong>{liveFohRoles.length ? liveFohRoles.join(", ") : "none"}</strong>. Unlocking
              later requires a reason and is recorded in the audit log.
            </p>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setFohCommitOpen(false)}
              >
                Cancel
              </button>
              <button className="payflow-modal-btn payflow-modal-btn-save" onClick={commitFohRoles}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockFohReason !== null && (
        <div className="payflow-modal-overlay" onClick={() => setUnlockFohReason(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Unlock FOH Roles</h2>
            <p className="payflow-modal-sub">
              This reopens the FOH-eligible roles for editing. The action is recorded in the audit
              log.
            </p>
            <label className="payflow-modal-label">
              Reason (required)
              <textarea
                className="payflow-modal-textarea"
                placeholder="Why are the FOH roles being unlocked?"
                value={unlockFohReason}
                autoFocus
                onChange={(e) => setUnlockFohReason(e.target.value)}
              />
            </label>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setUnlockFohReason(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                disabled={!unlockFohReason.trim()}
                onClick={confirmUnlockFoh}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {salariedEditOpen && (
        <div className="payflow-modal-overlay" onClick={() => setSalariedEditOpen(false)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Salaried Employees</h2>
            <p className="payflow-modal-sub">
              Tick the employees who are paid a fixed salary.
            </p>
            <div className="payflow-foh-picker">
              {availableEmployees.length === 0 ? (
                <div className="payflow-control-empty">No employees found in the payrolls yet.</div>
              ) : (
                availableEmployees.map((emp) => (
                  <label key={emp.id} className="payflow-foh-check">
                    <input
                      type="checkbox"
                      checked={salariedDraft.includes(emp.id)}
                      onChange={() => toggleSalariedDraft(emp.id)}
                    />
                    <span>{emp.name}</span>
                  </label>
                ))
              )}
            </div>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setSalariedEditOpen(false)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                onClick={saveSalariedEdit}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {salariedCommitOpen && (
        <div className="payflow-modal-overlay" onClick={() => setSalariedCommitOpen(false)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Commit Salaried Employees</h2>
            <p className="payflow-modal-sub">
              This locks who is treated as salaried:{" "}
              <strong>
                {liveSalariedEmployeeIds.length
                  ? liveSalariedEmployeeIds
                      .map((id) => availableEmployees.find((e) => e.id === id)?.name ?? id)
                      .join(", ")
                  : "none"}
              </strong>
              . Unlocking later requires a reason and is recorded in the audit log.
            </p>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setSalariedCommitOpen(false)}
              >
                Cancel
              </button>
              <button className="payflow-modal-btn payflow-modal-btn-save" onClick={commitSalaried}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockSalariedReason !== null && (
        <div className="payflow-modal-overlay" onClick={() => setUnlockSalariedReason(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Unlock Salaried Employees</h2>
            <p className="payflow-modal-sub">
              This reopens the salaried-employee list for editing. The action is recorded in the
              audit log.
            </p>
            <label className="payflow-modal-label">
              Reason (required)
              <textarea
                className="payflow-modal-textarea"
                placeholder="Why is the salaried list being unlocked?"
                value={unlockSalariedReason}
                autoFocus
                onChange={(e) => setUnlockSalariedReason(e.target.value)}
              />
            </label>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setUnlockSalariedReason(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                disabled={!unlockSalariedReason.trim()}
                onClick={confirmUnlockSalaried}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {holidayCommitOpen && (
        <div className="payflow-modal-overlay" onClick={() => setHolidayCommitOpen(false)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Commit Holiday Dates</h2>
            <p className="payflow-modal-sub">
              This will lock dates for Ontario Public Holidays for payroll purposes for the{" "}
              {currentYear} calendar year.
            </p>
            <div className="payflow-modal-actions">
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setHolidayCommitOpen(false)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                onClick={commitHolidays}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {adjustModal && (
        <div className="payflow-modal-overlay" onClick={() => setAdjustModal(null)}>
          <div className="payflow-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-modal-title">Manual Adjustment</h2>
            <p className="payflow-modal-sub">
              {adjustModal.employee} · {adjustModal.dayLabel}
            </p>

            <label className="payflow-modal-label">
              Adjustment (hours vs scheduled)
              <input
                className="payflow-modal-input"
                type="number"
                step="0.25"
                value={adjustModal.value}
                autoFocus
                onChange={(e) => setAdjustModal({ ...adjustModal, value: e.target.value })}
              />
            </label>

            {(() => {
              const worked = adjustModal.schedTotal + (parseFloat(adjustModal.value) || 0);
              const brk = breakDeductionFor(worked, breakRules);
              return (
                <div className="payflow-modal-net">
                  Net hours: <strong>{fmtHours(worked - brk)}</strong>{" "}
                  <span className="payflow-modal-net-note">
                    (scheduled {fmtHours(adjustModal.schedTotal)}
                    {brk > 0 ? `, break −${fmtHours(brk)}` : ""})
                  </span>
                </div>
              );
            })()}

            <label className="payflow-modal-label">
              Reason (required)
              <textarea
                className="payflow-modal-textarea"
                placeholder="Why is this entry being adjusted?"
                value={adjustModal.reason}
                onChange={(e) => setAdjustModal({ ...adjustModal, reason: e.target.value })}
              />
            </label>

            <div className="payflow-modal-actions">
              {adjustModal.isExisting && (
                <button
                  className="payflow-modal-btn payflow-modal-btn-remove"
                  onClick={() => {
                    payflowActions.clearAdjustment(
                      adjustModal.key,
                      currentUser,
                      `${adjustModal.employee} · ${adjustModal.dayLabel}`,
                    );
                    setAdjustModal(null);
                  }}
                >
                  Remove
                </button>
              )}
              <span className="payflow-modal-actions-spacer" />
              <button
                className="payflow-modal-btn payflow-modal-btn-cancel"
                onClick={() => setAdjustModal(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-modal-btn payflow-modal-btn-save"
                disabled={!adjustModal.reason.trim()}
                onClick={saveAdjustModal}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
